/**
 * Request-flow DBQ editor container.
 * Owns data loading, permissions, mutations, autosave, and the state passed into the
 * extracted chrome, layout, and dialog modules.
 */
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { Lock } from 'lucide-react';
import { toast } from 'react-toastify';

import useDbqForm from '../../hooks/useDbqForm';
import { useUser } from '../../contexts/UserContext';
import {
  canEditManagedMedicalOpinion,
  canEditDbqFields as canEditDbqFieldsByPermission,
  canCreateDbqSuggestion,
  canCommentOnDbq,
  canDismissDbqSuggestion,
  canManageDbqForms,
  canManageDbqMedicalOpinion,
  canManageDbqRequests,
  canResolveAnyDbqComment,
  canFinalizeDbqReview as canFinalizeDbqReviewByPermission,
  canReviewDbqSuggestions,
  isDbqCaseManagerActor,
  isProviderUser,
} from '../../lib/dbqPermissions';
import {
  isProviderOwnedRequest,
  isProviderReworkRound,
} from '../../lib/dbqWorkflow';
import { dbqStyles, dbqStandaloneStyles } from './dbq';
import {
  isMusculoskeletalDbq as isMusculoskeletalRequestFlowDbq,
  getMusculoskeletalSupplementalFieldDefinition,
  getMusculoskeletalSupplementalFieldValue,
  isMusculoskeletalSupplementalFieldId,
  normalizeMusculoskeletalSupplementalData,
  validateMusculoskeletalSupplementalData,
} from './dbq/dbqMusculoskeletalConstants';
import {
  useMarkDbqReadyForQaReview,
  useRequestMoAutoGeneration,
  useUpdateRequiredFields,
  useUpdateUserDbqRequiredFields,
  useFetchCurrentProviderProfile,
  useSignDbq,
  useCreateDbqSuggestion,
  upsertDbqSuggestion,
  useAcceptDbqSuggestion,
  useRejectDbqSuggestion,
  useDismissDbqSuggestion,
} from '../../services/userDbq';
import { useFetchJobStatus } from '../../services/casegpt';
import { saveMoRemarksFullText } from '../../services/medicalOpinion';
import { findDbqFieldById, findDbqFieldGroupLabel } from './dbq/dbqSuggestionUtils';
import DbqChangesPanel, {
  countLiveChanges,
} from './dbq/DbqChangesPanel';
import { findNearestRemainingReviewField, reviewFieldToMoveTo } from './dbq/dbqReviewNavigation';
import { buildRoleScopedSavePayload } from './dbq/dbqEditorSaveUtils';
import {
  AI_DRAFT_LABELS,
  EMPTY_SUGGESTION_STATS,
  areInlineSuggestionValuesEqual,
  areOrderedFieldIdsEqual,
  buildAiReviewNotice,
  buildInlineMissingInputStateKey,
  buildInlineMusculoskeletalSupplementalData,
  buildInlineSuggestionDraftsFromFormData,
  buildMissingDetailEvidencePatch,
  buildSuggestionStatsFromItems,
  doesParentOptionSelectionRevealField,
  evaluateInlineCondition,
  findRemarksFieldIdInElements,
  flattenVisibleIfConditions,
  getAiGenerationNoticeTargetFieldIds,
  getChangedMusculoskeletalSupplementalFieldIds,
  getContentionDisplayLabel,
  getEditableAutoMedicalOpinionText,
  getInlineSuggestionActionSource,
  getInlineSuggestionEvidenceRefs,
  getInlineSuggestionMissingInputs,
  getInlineSuggestionTranscriptRefs,
  getInlineSuggestionValue,
  getLatestInlineAiNotice,
  getLatestInlineSuggestionWithStatus,
  getLatestPendingInlineSuggestion,
  getInlineSuggestionsWithStatus,
  getMoToeLabel,
  getUserDbqAiPollingInterval,
  hasActiveDbqAiGeneration,
  hasMissingDetailEvidencePatch,
  hasOwnValue,
  inlineSuggestionHasMissingDetail,
  INLINE_SUGGESTION_NAVIGATION_STATUSES,
  INLINE_SUGGESTION_VIEW_MODES,
  isAiGeneratedInlineSuggestion,
  isActiveDbqAiGenerationStatus,
  isExaminerCertificationAndSignatureSection,
  isInlineAiNotice,
  isInlineGenerationFailureNotice,
  isInlineProgressNotice,
  isDbqValuePresent,
  mergeMissingDetailEvidencePatch,
  MISSING_USER_INPUT_TOKEN,
  orderFieldIdsByRenderedPosition,
  parseStoredRequestContentions,
  replaceInlineMissingInputOccurrence,
  SUGGESTION_STATUS_FILTER_ORDER,
  SUGGESTION_STATUS_FILTERS,
} from './dbq/requestFlow/userDbqEditorRequestFlowUtils';
import fixedHeaderStyles from './dbq/requestFlow/userDbqEditorRequestFlowStyles';
import UserDbqEditorRequestFlowChrome from './dbq/requestFlow/UserDbqEditorRequestFlowChrome';
import UserDbqEditorRequestFlowDialogs from './dbq/requestFlow/UserDbqEditorRequestFlowDialogs';
import UserDbqEditorRequestFlowLayout from './dbq/requestFlow/UserDbqEditorRequestFlowLayout';

const UserDbqEditor = () => {
  const { userDbqId } = useParams();
  const { user, roles, permissions } = useUser();
  const queryClient = useQueryClient();
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeReviewFieldId, setActiveReviewFieldId] = useState(null);
  const [activeReviewFieldLabel, setActiveReviewFieldLabel] = useState('');
  const [activeReviewTab, setActiveReviewTab] = useState('comments');
  const [
    pendingSuggestionComposerFieldId,
    setPendingSuggestionComposerFieldId,
  ] = useState(null);
  const [activeSuggestionStatusFilter, setActiveSuggestionStatusFilter] =
    useState(SUGGESTION_STATUS_FILTERS.pending);
  const [requiredFields, setRequiredFields] = useState([]);
  const [templateRequiredFields, setTemplateRequiredFields] = useState([]);
  const [userRequiredFields, setUserRequiredFields] = useState([]);
  const [showRequiredFieldModal, setShowRequiredFieldModal] = useState(false);
  const [pendingRequiredFieldId, setPendingRequiredFieldId] = useState(null);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [showFieldConfirmation, setShowFieldConfirmation] = useState(false);
  const [pendingFieldChange, setPendingFieldChange] = useState(null);
  const [moGenerationJobId, setMoGenerationJobId] = useState(null);
  const [showMoGenerationModal, setShowMoGenerationModal] = useState(false);
  const [moGenerationPromptInput, setMoGenerationPromptInput] = useState('');
  const [showMoEvidenceModal, setShowMoEvidenceModal] = useState(false);
  const [showRemarksEditorModal, setShowRemarksEditorModal] = useState(false);
  const [remarksEditorText, setRemarksEditorText] = useState('');
  const [remarksEditorSubmitMode, setRemarksEditorSubmitMode] = useState(null);
  const [certificationData, setCertificationData] = useState({
    signature: '',
    nameAndTitle: '',
    specialty: '',
    phoneOrFax: '',
    npi: '',
    licenseNumberAndState: '',
    address: '',
    dateSigned: new Date().toISOString().split('T')[0],
  });
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const saveTimeoutRef = useRef(null);
  const lastMoJobTerminalStatusRef = useRef(null);
  const pendingSuggestionReviewNavigationRef = useRef(null);
  const previousFilteredSuggestionFieldIdsRef = useRef([]);
  const skipNextAutoSaveRef = useRef(true);
  const previousInlineSuggestionNavigatorFieldIdsRef = useRef([]);
  const hasManuallyNavigatedInlineSuggestionNavigatorRef = useRef(false);
  const [inlineSuggestionDrafts, setInlineSuggestionDrafts] = useState({});
  const [inlineSuggestionDirtyFieldIds, setInlineSuggestionDirtyFieldIds] =
    useState(() => new Set());
  const inlineSuggestionDraftsRef = useRef(inlineSuggestionDrafts);
  const inlineSuggestionDirtyFieldIdsRef = useRef(
    inlineSuggestionDirtyFieldIds,
  );
  const [inlineSuggestionRevision, setInlineSuggestionRevision] = useState(0);
  const [inlineSuggestionViewMode, setInlineSuggestionViewMode] = useState(
    INLINE_SUGGESTION_VIEW_MODES.suggestions,
  );
  const [
    activeInlineSuggestionNavigatorFieldId,
    setActiveInlineSuggestionNavigatorFieldId,
  ] = useState(null);

  useEffect(() => {
    inlineSuggestionDraftsRef.current = inlineSuggestionDrafts;
  }, [inlineSuggestionDrafts]);

  useEffect(() => {
    inlineSuggestionDirtyFieldIdsRef.current = inlineSuggestionDirtyFieldIds;
  }, [inlineSuggestionDirtyFieldIds]);
  const [
    orderedInlineSuggestionNavigatorFieldIds,
    setOrderedInlineSuggestionNavigatorFieldIds,
  ] = useState([]);
  const [inlineMissingDetailSuggestion, setInlineMissingDetailSuggestion] =
    useState(null);
  const [inlineMissingDetailFieldId, setInlineMissingDetailFieldId] =
    useState(null);
  const [inlineMissingDetailDraftText, setInlineMissingDetailDraftText] =
    useState('');
  const [inlineMissingInputValues, setInlineMissingInputValues] = useState({});
  const [inlineMissingInputSearches, setInlineMissingInputSearches] = useState(
    {},
  );
  const [
    inlineMissingDetailEvidencePatch,
    setInlineMissingDetailEvidencePatch,
  ] = useState({
    sourceEvidenceRefs: [],
    transcriptRefs: [],
  });
  const [isInlineMissingDetailSaving, setIsInlineMissingDetailSaving] =
    useState(false);
  const [inlineEvidenceSuggestion, setInlineEvidenceSuggestion] =
    useState(null);
  const [inlineEvidenceFieldId, setInlineEvidenceFieldId] = useState(null);
  const [inlineEvidencePage, setInlineEvidencePage] = useState({
    fileId: null,
    fileName: null,
    pageNumber: null,
  });
  const [inlineGenerationFailureNotice, setInlineGenerationFailureNotice] =
    useState(null);
  const [inlineGenerationFailureFieldId, setInlineGenerationFailureFieldId] =
    useState(null);
  const [
    inlineRejectedSuggestionsFieldId,
    setInlineRejectedSuggestionsFieldId,
  ] = useState(null);
  // QA "Changes since signing" panel (claims:approve_for_va only).
  const [showChangesPanel, setShowChangesPanel] = useState(false);

  const dbqAccess = useMemo(
    () => ({ roles, permissions }),
    [permissions, roles],
  );
  const canManageRequestWorkspace = canManageDbqRequests(dbqAccess);
  const canManageFormTemplates = canManageDbqForms(dbqAccess);
  const canManageMedicalOpinion = canManageDbqMedicalOpinion(dbqAccess);
  const isProvider = isProviderUser(dbqAccess);
  const isCaseManagerActor = isDbqCaseManagerActor(dbqAccess);
  const canFinalizeDbqReview = canFinalizeDbqReviewByPermission(dbqAccess);
  const canEditDbqFields = canEditDbqFieldsByPermission(dbqAccess);
  const canToggleRequiredPerDbq = canManageFormTemplates || canEditDbqFields;
  const canResolveAnyComment = canResolveAnyDbqComment(dbqAccess);
  const canSuggest = canCreateDbqSuggestion(dbqAccess);
  const canAcceptSuggestions = canReviewDbqSuggestions(dbqAccess);
  const canDismissSuggestions = canDismissDbqSuggestion(dbqAccess);
  const canComment = canCommentOnDbq(dbqAccess);
  const invalidateDbqAndAuditQueries = useCallback(() => {
    queryClient.invalidateQueries(['userDbq', userDbqId]);
    queryClient.invalidateQueries(['user-dbq-audit', userDbqId]);
  }, [queryClient, userDbqId]);
  const { data: moGenerationJobStatus } = useFetchJobStatus(moGenerationJobId, {
    enabled: Boolean(canManageMedicalOpinion && moGenerationJobId),
    onSuccess: (job) => {
      const status = job?.jobStatus;
      if (!status) return;

      if (status === 'pending' || status === 'processing') {
        lastMoJobTerminalStatusRef.current = null;
        return;
      }

      invalidateDbqAndAuditQueries();

      if (lastMoJobTerminalStatusRef.current === status) return;
      lastMoJobTerminalStatusRef.current = status;

      if (status === 'completed') {
        toast.success('Medical Opinion draft generated for review.');
      } else if (status === 'failed') {
        toast.error(job?.errorMessage || 'Medical Opinion generation failed.');
      }

      setMoGenerationJobId(null);
    },
  });

  const requestMoGenerationMutation = useRequestMoAutoGeneration(
    (result) => {
      if (result?.alreadyRunning) {
        toast.info(
          result?.message ||
            'Medical Opinion generation is already in progress for this DBQ.',
        );
      } else {
        toast.success(result?.message || 'Medical Opinion generation started.');
      }

      setShowMoGenerationModal(false);
      setMoGenerationPromptInput('');

      if (result?.moJob?.jobId) {
        setMoGenerationJobId(result.moJob.jobId);
      }

      invalidateDbqAndAuditQueries();
    },
    (error) => {
      toast.error(
        error?.response?.data?.msg ||
          'Failed to start Medical Opinion generation',
      );
    },
  );

  // Fetch current provider profile (for signing modal) - only for providers
  const { data: providerProfileData } =
    useFetchCurrentProviderProfile(isProvider);
  const providerProfile = isProvider ? providerProfileData?.provider : null;

  // Preload signature image so it's cached when modal opens
  useEffect(() => {
    if (providerProfile?.providerSignatureUrl) {
      const img = new window.Image();
      img.src = providerProfile.providerSignatureUrl;
    }
  }, [providerProfile?.providerSignatureUrl]);

  // Get provider profiles (memoized to prevent unnecessary re-renders)
  const providerProfiles = useMemo(() => {
    return providerProfile?.profiles || [];
  }, [providerProfile]);

  // Initialize certification data from provider profile (using default or first profile)
  useEffect(() => {
    if (providerProfile && providerProfiles.length > 0) {
      // Find default profile, or use first
      const defaultProfile =
        providerProfiles.find((p) => p.isDefault) || providerProfiles[0];
      setSelectedProfileId(defaultProfile.profileId);

      const fullName =
        `${providerProfile.userFirstName || ''} ${providerProfile.userLastName || ''}`.trim();
      const nameWithTitle = defaultProfile.title
        ? `${fullName}, ${defaultProfile.title}`
        : fullName;

      const licenseInfo =
        defaultProfile.licenseNumber && defaultProfile.licenseState
          ? `${defaultProfile.licenseNumber} — ${defaultProfile.licenseState}`
          : defaultProfile.licenseNumber || '';

      setCertificationData({
        signature: fullName,
        nameAndTitle: nameWithTitle,
        specialty: defaultProfile.specialty || '',
        phoneOrFax:
          defaultProfile.businessPhone || providerProfile.userPhone || '',
        npi: defaultProfile.npi || '',
        licenseNumberAndState: licenseInfo,
        address: defaultProfile.address || '',
        dateSigned: new Date().toISOString().split('T')[0],
      });
    }
  }, [providerProfile, providerProfiles]);

  // Sign DBQ mutation
  const signDbqMutation = useSignDbq(
    () => {
      toast.success(
        'DBQ signed and submitted successfully! The claim manager has been notified.',
      );
      setShowSigningModal(false);
      invalidateDbqAndAuditQueries();
    },
    (error) => {
      toast.error(error?.response?.data?.msg || 'Failed to sign DBQ');
    },
  );

  const markReadyForReviewMutation = useMarkDbqReadyForQaReview(
    () => {
      toast.success('DBQ marked ready for Claims Admin review.');
      invalidateDbqAndAuditQueries();
      queryClient.invalidateQueries({ queryKey: ['user-dbqs'] });
    },
    (error) => {
      toast.error(
        error?.response?.data?.msg || 'Failed to mark DBQ ready for review',
      );
    },
  );

  // Mutation for updating template-level required fields (requires admin:dbq_forms)
  const updateRequiredFieldsMutation = useUpdateRequiredFields(
    () => {
      queryClient.invalidateQueries(['userDbq', userDbqId]);
      toast.success('Required fields updated for all DBQs of this type');
    },
    (error) => {
      toast.error(
        error?.response?.data?.msg || 'Failed to update required fields',
      );
    },
  );

  // Mutation for updating user DBQ's required fields (requires admin:dbq_forms)
  const updateUserDbqRequiredFieldsMutation = useUpdateUserDbqRequiredFields(
    () => {
      queryClient.invalidateQueries(['userDbq', userDbqId]);
      toast.success('Required fields updated for this DBQ only');
    },
    (error) => {
      toast.error(
        error?.response?.data?.msg || 'Failed to update required fields',
      );
    },
  );

  // State for newly required fields modal
  const [showNewlyRequiredModal, setShowNewlyRequiredModal] = useState(false);
  const [newlyRequiredFields, setNewlyRequiredFields] = useState([]);

  // State for missing required fields validation panel (replaces native alert)
  const [missingRequiredFieldsList, setMissingRequiredFieldsList] = useState(
    [],
  );
  const [supplementalValidationErrors, setSupplementalValidationErrors] =
    useState([]);
  const supplementalSectionRef = useRef(null);

  // Use shared form hook
  const formContext = useDbqForm();
  const {
    formData,
    setFormData,
    repeatingInstances,
    initializeRepeatingInstances,
    filterFormDataByActiveInstances,
    filterHiddenFieldValues,
    buildRenderWithGroupMap,
    buildFieldKey,
    getFieldValue,
    isVisible,
    setFormJson,
    handleFieldChange,
    getFieldComments,
    addComment,
    addReply,
    resolveComment,
    getAllActiveCommentFieldIds,
    getFieldCommentCount,
    getFieldSuggestions,
    getAllSuggestionFieldIds,
    getAllReviewFieldIds,
    getMissingRequiredFields,
  } = formContext;

  // Stale-tab guard state (see the staleness block further down for the
  // detection query): declared early because save/mutation handlers below
  // advance the baseline.
  const knownUpdatedAtRef = useRef(0);
  const [isStale, setIsStale] = useState(false);
  const [staleActorName, setStaleActorName] = useState(null);
  const isStaleRef = useRef(false);
  isStaleRef.current = isStale;
  // While our own save is in flight, staleness verdicts are ignored: the
  // check could read the row AFTER our write but BEFORE the save response
  // advances the baseline, making our own save look like someone else's
  // (a stuck self-triggered banner). The next interval re-checks.
  const saveInFlightRef = useRef(false);
  const advanceKnownUpdatedAt = useCallback((updatedAt) => {
    const ts = updatedAt ? new Date(updatedAt).getTime() : 0;
    if (ts && ts > knownUpdatedAtRef.current) knownUpdatedAtRef.current = ts;
  }, []);

  const syncDbqMutationResult = useCallback(
    (result, successMessage) => {
      if (result?.userDbq?.updatedAt) {
        advanceKnownUpdatedAt(result.userDbq.updatedAt);
      }
      if (result?.userDbq?.formData) {
        skipNextAutoSaveRef.current = true;
        setFormData(result.userDbq.formData);
        queryClient.setQueryData(['userDbq', userDbqId], (current) => {
          if (!current?.userDbq) return current;
          return {
            ...current,
            userDbq: {
              ...current.userDbq,
              ...result.userDbq,
            },
          };
        });
      }

      queryClient.invalidateQueries(['userDbq', userDbqId]);
      queryClient.invalidateQueries(['user-dbq-audit', userDbqId]);

      if (successMessage) {
        toast.success(successMessage);
      }
    },
    [advanceKnownUpdatedAt, queryClient, setFormData, userDbqId],
  );
  const saveRemarksFullTextMutation = useMutation({
    mutationFn: async ({ fullMedicalOpinionText }) =>
      saveMoRemarksFullText(userDbqId, fullMedicalOpinionText),
    onSuccess: (result) => {
      syncDbqMutationResult(result, 'Medical Opinion draft updated');
      setShowRemarksEditorModal(false);
      setRemarksEditorSubmitMode(null);
    },
    onError: (error) => {
      setRemarksEditorSubmitMode(null);
      toast.error(
        error?.response?.data?.msg || 'Failed to save full Medical Opinion',
      );
    },
  });

  const createSuggestionMutation = useCreateDbqSuggestion(
    (result) => {
      syncDbqMutationResult(result, 'Suggestion added');
    },
    (error) => {
      toast.error(error?.response?.data?.msg || 'Failed to add suggestion');
    },
  );

  const acceptSuggestionMutation = useAcceptDbqSuggestion(
    (result) => {
      syncDbqMutationResult(result, 'Suggestion accepted');
    },
    (error) => {
      toast.error(error?.response?.data?.msg || 'Failed to accept suggestion');
    },
  );

  const rejectSuggestionMutation = useRejectDbqSuggestion(
    (result) => {
      syncDbqMutationResult(result, 'Suggestion rejected');
    },
    (error) => {
      toast.error(error?.response?.data?.msg || 'Failed to reject suggestion');
    },
  );

  const dismissSuggestionMutation = useDismissDbqSuggestion(
    (result) => {
      syncDbqMutationResult(result, 'Suggestion removed');
    },
    (error) => {
      toast.error(error?.response?.data?.msg || 'Failed to dismiss suggestion');
    },
  );

  // Wrap handleFieldChange to show modal when fields become required
  const wrappedHandleFieldChange = useCallback(
    (fieldId, value, instancePath = []) => {
      // Clear validation panel when user starts editing fields
      if (missingRequiredFieldsList.length > 0) {
        setMissingRequiredFieldsList([]);
      }

      const { newlyRequired } = handleFieldChange(fieldId, value, instancePath);
      if (newlyRequired?.length > 0) {
        setNewlyRequiredFields(newlyRequired);
        setShowNewlyRequiredModal(true);
      }
    },
    [handleFieldChange, missingRequiredFieldsList.length],
  );

  // Create modified formContext with wrapped handler
  const wrappedFormContext = useMemo(
    () => ({
      ...formContext,
      handleFieldChange: wrappedHandleFieldChange,
    }),
    [formContext, wrappedHandleFieldChange],
  );

  // Fetch user DBQ (requires authentication)
  const { data, isLoading, error } = useQuery({
    queryKey: ['userDbq', userDbqId],
    queryFn: async () => {
      const response = await axios.get(`/dbq/user-dbq/${userDbqId}`);
      return response.data;
    },
    refetchOnWindowFocus: false,
    refetchInterval: getUserDbqAiPollingInterval,
    refetchIntervalInBackground: true,
  });

  const userDbq = data?.userDbq;
  const isMusculoskeletal = useMemo(
    () => isMusculoskeletalRequestFlowDbq(userDbq || {}),
    [userDbq],
  );
  const requestWorkflowStatus = userDbq?.request?.status || null;

  // Signed state (2026-08-04 rework): signing no longer locks anything - it
  // switches the DBQ into RECORDED-edit mode (every change stamps
  // _postSignEdits for the QA Changes panel). The only hard lock is a
  // FINALIZED request: read-only everywhere, server 403s writes too.
  const providerSigned =
    Boolean(formData?._certification?.signedAt) ||
    userDbq?.status === 'complete';
  const finalizedLocked = requestWorkflowStatus === 'finalized';
  const postSignEdits =
    formData?._postSignEdits && typeof formData._postSignEdits === 'object'
      ? formData._postSignEdits
      : null;
  // LIVE count (fields currently differing from signed) so the button and
  // panel agree instantly after a Swap back, without waiting for the
  // autosave to round-trip the cleared stamp.
  const postSignEditCount = useMemo(
    () => countLiveChanges(postSignEdits, formData),
    [postSignEdits, formData],
  );
  // ---------------------------------------------------------------------
  // Stale-tab guard: every tab tracks the newest updatedAt it has produced
  // or received. A background check (interval + window focus) compares the
  // server's updatedAt against that baseline; when someone ELSE has saved,
  // a sticky banner appears and this tab's saves pause until refresh - a
  // stale tab can never silently overwrite another person's work. Own saves
  // advance the baseline from their responses, so they never self-trigger.
  // (State lives near the top of the component; handlers there advance it.)
  //
  // The main query resynced this tab to server truth (initial load or any
  // refetch): adopt that baseline and clear the stale flag.
  useEffect(() => {
    if (userDbq?.updatedAt) {
      advanceKnownUpdatedAt(userDbq.updatedAt);
      setIsStale(false);
      setStaleActorName(null);
    }
  }, [userDbq?.updatedAt, advanceKnownUpdatedAt]);

  useQuery({
    queryKey: ['userDbq-staleness', userDbqId],
    queryFn: async () => {
      const response = await axios.get(`/dbq/user-dbq/${userDbqId}`);
      return response.data;
    },
    enabled: Boolean(userDbq) && !isStale,
    refetchInterval: 45000,
    // Keep checking while the tab is backgrounded so the banner is already
    // showing when the user switches back (focus also triggers a check).
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    // This query exists ONLY for the onSuccess comparison below - it must
    // never re-render the (heavy) editor. Without this, every 45s poll and
    // every window-focus check re-rendered the whole form.
    notifyOnChangeProps: [],
    onSuccess: (freshData) => {
      if (saveInFlightRef.current) return;
      const serverTs = freshData?.userDbq?.updatedAt
        ? new Date(freshData.userDbq.updatedAt).getTime()
        : 0;
      if (serverTs && serverTs > knownUpdatedAtRef.current) {
        const staleSinceTs = knownUpdatedAtRef.current;
        setIsStale(true);
        // Name the other editor for the banner (visibility only): the newest
        // audit event since our baseline carries the actor. Best-effort -
        // viewers without audit access just get the generic wording.
        axios
          .get(`/dbq/user-dbq/${userDbqId}/audit`)
          .then((auditResponse) => {
            const events = Array.isArray(auditResponse?.data?.events)
              ? auditResponse.data.events
              : [];
            const newest = events.find((event) => event?.actorName);
            if (
              newest?.actorName &&
              (!staleSinceTs ||
                new Date(newest.eventDate).getTime() >= staleSinceTs)
            ) {
              setStaleActorName(newest.actorName);
            }
          })
          .catch(() => {});
      }
    },
  });

  const isProviderRework = isProviderReworkRound(userDbq?.request);
  const isCaseManagerProviderReviewLocked =
    isCaseManagerActor && isProviderOwnedRequest(userDbq?.request);
  const canCreateSuggestions = canSuggest && !isCaseManagerProviderReviewLocked;
  const isClaimsAdminInlineSuggestionActor =
    canFinalizeDbqReview && !isProvider && !canManageFormTemplates;
  const isFullAdminInlineSuggestionActor =
    canManageFormTemplates && !isProvider;
  // Inline suggestion editing is now broadly available during the first internal
  // review round. Expanded AI field generation remains claim-gated at request creation.
  const canUseInlineSuggestionMode =
    requestWorkflowStatus === 'ready_to_send' &&
    (isCaseManagerActor ||
      isClaimsAdminInlineSuggestionActor ||
      isFullAdminInlineSuggestionActor);
  const useInlineSuggestionMode = canUseInlineSuggestionMode;
  const isInlineCurrentDbqView =
    useInlineSuggestionMode &&
    inlineSuggestionViewMode === INLINE_SUGGESTION_VIEW_MODES.currentDbq;
  const isInlineSuggestionsView =
    useInlineSuggestionMode && !isInlineCurrentDbqView;
  const canDismissSuggestionEdits =
    canDismissSuggestions && !isCaseManagerProviderReviewLocked;
  const canCreateComments = canComment && !isCaseManagerProviderReviewLocked;
  const moRequestContentions = useMemo(() => {
    const request = userDbq?.request || {};
    const requestContentions = Array.isArray(request.requestContentions)
      ? request.requestContentions
          .filter(
            (requestContention) =>
              requestContention?.contention || requestContention?.contentionKey,
          )
          .map((requestContention, index) => {
            const contention =
              requestContention.contention ||
              requestContention.contentionKey ||
              '';
            const resolvedToeId = requestContention.toeId || '';

            return {
              contention,
              contentionLabel:
                requestContention.contentionLabel ||
                getContentionDisplayLabel(contention),
              toeId: resolvedToeId,
              toeLabel: getMoToeLabel(requestContention.toe, resolvedToeId),
              sortOrder: Number.isInteger(Number(requestContention.sortOrder))
                ? Number(requestContention.sortOrder)
                : index,
            };
          })
      : [];

    if (requestContentions.length > 0) {
      return requestContentions;
    }

    const legacyContentions = parseStoredRequestContentions(request.contention);
    const legacyToeId = request.toeId || userDbq?.toeId || '';

    return legacyContentions.map((contention, index) => ({
      contention,
      contentionLabel: getContentionDisplayLabel(contention),
      toeId: legacyToeId,
      toeLabel: getMoToeLabel(request.toe, legacyToeId),
      sortOrder: index,
    }));
  }, [userDbq]);
  const hasMissingMoToe = moRequestContentions.some(
    (requestContention) => !requestContention.toeId,
  );
  const canStartMoGeneration =
    moRequestContentions.length > 0 && !hasMissingMoToe;
  const canMarkReadyForReview = useMemo(() => {
    if (!(canManageRequestWorkspace || canFinalizeDbqReview)) return false;
    return requestWorkflowStatus === 'completed';
  }, [canFinalizeDbqReview, canManageRequestWorkspace, requestWorkflowStatus]);
  const reviewActionLabel = 'Mark Ready For Review';
  const canProviderSubmit = useMemo(() => {
    if (!isProvider) return false;

    if (requestWorkflowStatus) {
      if (requestWorkflowStatus === 'changes_requested') {
        return isProviderRework;
      }

      return ['sent_to_provider', 'provider_in_progress'].includes(
        requestWorkflowStatus,
      );
    }

    return ['provider_ready', 'corrections'].includes(
      userDbq?.status || 'draft',
    );
  }, [isProvider, isProviderRework, requestWorkflowStatus, userDbq?.status]);
  const isProviderConfirmingChanges = isProvider && isProviderRework;
  const providerSignActionLabel = isProviderConfirmingChanges
    ? 'Confirm Changes'
    : 'Sign & Submit';
  const rawFormJson = userDbq?.formJson;

  // On the user DBQ edit page, do NOT render the final "EXAMINER'S CERTIFICATION AND SIGNATURE" section.
  // Its contents are collected/finalized via the provider "Sign & Submit" flow.
  const formJson = useMemo(() => {
    if (!rawFormJson?.sections?.length) return rawFormJson;

    const lastSection = rawFormJson.sections[rawFormJson.sections.length - 1];
    if (!isExaminerCertificationAndSignatureSection(lastSection))
      return rawFormJson;

    return {
      ...rawFormJson,
      sections: rawFormJson.sections.slice(0, -1),
    };
  }, [rawFormJson]);
  const canEditMusculoskeletalSupplemental =
    canEditDbqFields && !isCaseManagerProviderReviewLocked;
  const musculoskeletalSupplementalData = useMemo(
    () =>
      normalizeMusculoskeletalSupplementalData(
        formData?._supplemental?.musculoskeletal,
      ),
    [formData?._supplemental?.musculoskeletal],
  );
  const supplementalInsertAfterSectionId = useMemo(() => {
    if (!isMusculoskeletal || !formJson?.sections?.length) {
      return null;
    }

    const visibleSections = formJson.sections.filter((section) =>
      isVisible(section?.visible_if, []),
    );

    const remarksSection = visibleSections.find((section) =>
      Boolean(findRemarksFieldIdInElements(section.elements || [])),
    );

    if (remarksSection?.id) {
      return remarksSection.id;
    }

    return visibleSections[visibleSections.length - 1]?.id || null;
  }, [formJson, isMusculoskeletal, isVisible]);

  const autoMedicalOpinionData = useMemo(() => {
    const value = userDbq?.formData?._autoMedicalOpinion;
    return value && typeof value === 'object' ? value : null;
  }, [userDbq?.formData?._autoMedicalOpinion]);
  const evidenceReviewGenerationData = useMemo(() => {
    const value = userDbq?.formData?._aiFieldGeneration?.evidence_review;
    return value && typeof value === 'object' ? value : null;
  }, [userDbq?.formData?._aiFieldGeneration?.evidence_review]);
  const aiFieldGenerationBucket = useMemo(() => {
    const value = userDbq?.formData?._aiFieldGeneration;
    return value && typeof value === 'object' ? value : {};
  }, [userDbq?.formData?._aiFieldGeneration]);
  const editableAutoMedicalOpinionText = useMemo(
    () => getEditableAutoMedicalOpinionText(autoMedicalOpinionData),
    [autoMedicalOpinionData],
  );

  useEffect(() => {
    if (
      !useInlineSuggestionMode &&
      inlineSuggestionViewMode !== INLINE_SUGGESTION_VIEW_MODES.suggestions
    ) {
      setInlineSuggestionViewMode(INLINE_SUGGESTION_VIEW_MODES.suggestions);
    }
  }, [inlineSuggestionViewMode, useInlineSuggestionMode]);

  const remarksFieldId = useMemo(() => {
    if (autoMedicalOpinionData?.insertedFieldId) {
      return autoMedicalOpinionData.insertedFieldId;
    }

    if (!formJson?.sections?.length) return null;
    for (const section of formJson.sections) {
      const fieldId = findRemarksFieldIdInElements(section.elements || []);
      if (fieldId) return fieldId;
    }
    return null;
  }, [autoMedicalOpinionData?.insertedFieldId, formJson]);
  const remarksFieldInfo = useMemo(() => {
    if (!remarksFieldId) return null;

    const remarksField = findDbqFieldById(formJson, remarksFieldId);
    if (!remarksField) return null;

    const maxLengthValue = Number(remarksField?.validation?.max_length);
    const charsPerLineValue = Number(remarksField?.validation?.chars_per_line);

    return {
      fieldId: remarksField.id || remarksFieldId,
      label: remarksField.label || null,
      maxLength:
        Number.isFinite(maxLengthValue) && maxLengthValue > 0
          ? maxLengthValue
          : null,
      charsPerLine:
        Number.isFinite(charsPerLineValue) && charsPerLineValue > 0
          ? charsPerLineValue
          : null,
    };
  }, [formJson, remarksFieldId]);
  const canManageMoRemarks = Boolean(
    canEditManagedMedicalOpinion(dbqAccess) && remarksFieldId,
  );
  const canViewMoRemarks = Boolean(
    (canManageMoRemarks || isCaseManagerActor) && remarksFieldId,
  );
  const activeMoDraftSuggestionStatus =
    String(autoMedicalOpinionData?.draftSuggestionStatus || '')
      .trim()
      .toLowerCase() || null;
  const isManagedMedicalOpinionRemarks = Boolean(
    canViewMoRemarks &&
      editableAutoMedicalOpinionText &&
      activeMoDraftSuggestionStatus !== 'rejected' &&
      activeMoDraftSuggestionStatus !== 'dismissed',
  );
  const isManagedMedicalOpinionTruncated = useMemo(() => {
    if (!isManagedMedicalOpinionRemarks) return false;
    if (activeMoDraftSuggestionStatus !== 'accepted') return false;
    if (typeof autoMedicalOpinionData?.isRemarksTruncated === 'boolean') {
      return autoMedicalOpinionData.isRemarksTruncated;
    }
    return (
      String(formData?.[remarksFieldId] || '') !==
      editableAutoMedicalOpinionText
    );
  }, [
    activeMoDraftSuggestionStatus,
    autoMedicalOpinionData?.isRemarksTruncated,
    editableAutoMedicalOpinionText,
    formData,
    isManagedMedicalOpinionRemarks,
    remarksFieldId,
  ]);

  const moGenerationStatus =
    moGenerationJobStatus?.jobStatus || autoMedicalOpinionData?.status || null;
  const isMoGenerationActive =
    moGenerationStatus === 'pending' || moGenerationStatus === 'processing';
  const moGenerationProgressText =
    moGenerationJobStatus?.totalItems > 0
      ? `${moGenerationJobStatus.processedItems || 0}/${moGenerationJobStatus.totalItems}`
      : null;

  useEffect(() => {
    const existingJobId = autoMedicalOpinionData?.jobId;
    if (!existingJobId) return;
    if (moGenerationStatus !== 'pending' && moGenerationStatus !== 'processing')
      return;
    if (moGenerationJobId) return;
    setMoGenerationJobId(existingJobId);
  }, [autoMedicalOpinionData?.jobId, moGenerationStatus, moGenerationJobId]);

  // Set formJson in the hook so handleFieldChange can clear dependent fields
  useEffect(() => {
    if (formJson) {
      setFormJson(formJson);
    }
  }, [formJson, setFormJson]);

  // Helper to find parent option that contains a field as a child_element
  const findParentOptionForField = useCallback(
    (targetFieldId, elements, parentFieldId = null, parentOptionId = null) => {
      for (const el of elements || []) {
        if (el.id === targetFieldId) {
          // Found it - return the parent option info
          return parentFieldId && parentOptionId
            ? { fieldId: parentFieldId, optionId: parentOptionId }
            : null;
        }
        // Check if this field has options with child_elements
        if (el.options) {
          for (const opt of el.options) {
            if (opt.child_elements) {
              const result = findParentOptionForField(
                targetFieldId,
                opt.child_elements,
                el.id,
                opt.id,
              );
              if (result) return result;
            }
          }
        }
        // Check nested elements
        if (el.elements) {
          const result = findParentOptionForField(
            targetFieldId,
            el.elements,
            parentFieldId,
            parentOptionId,
          );
          if (result) return result;
        }
      }
      return null;
    },
    [],
  );

  const logAiReviewNavigation = useCallback((message, payload = {}) => {
    console.log('[DBQ AI Review Navigation]', message, payload);
  }, []);

  const findNavigableElementForField = useCallback(
    ({ fieldId, optionId = null, debugContext = null } = {}) => {
      const normalizedFieldId = String(fieldId || '').trim();
      if (!normalizedFieldId) {
        if (debugContext) {
          logAiReviewNavigation(
            'No field id available for navigation lookup.',
            { debugContext },
          );
        }
        return null;
      }

      if (optionId) {
        const directOptionElement = document.getElementById(
          `${normalizedFieldId}_${optionId}`,
        );
        if (directOptionElement) {
          if (debugContext) {
            logAiReviewNavigation('Resolved direct option element.', {
              debugContext,
              fieldId: normalizedFieldId,
              optionId,
            });
          }
          return directOptionElement;
        }
      }

      const directFieldElement = document.querySelector(
        `[data-field-id="${normalizedFieldId}"]`,
      );
      if (directFieldElement) {
        if (debugContext) {
          logAiReviewNavigation('Resolved direct field element.', {
            debugContext,
            fieldId: normalizedFieldId,
          });
        }
        return directFieldElement;
      }

      if (!formJson?.sections) {
        if (debugContext) {
          logAiReviewNavigation(
            'Form schema is unavailable during navigation lookup.',
            {
              debugContext,
              fieldId: normalizedFieldId,
            },
          );
        }
        return null;
      }

      for (const section of formJson.sections) {
        const parentOption = findParentOptionForField(
          normalizedFieldId,
          section.elements,
        );
        if (!parentOption?.fieldId) continue;

        if (parentOption.optionId) {
          const parentOptionElement = document.getElementById(
            `${parentOption.fieldId}_${parentOption.optionId}`,
          );
          if (parentOptionElement) {
            if (debugContext) {
              logAiReviewNavigation('Resolved parent option element.', {
                debugContext,
                fieldId: normalizedFieldId,
                parentFieldId: parentOption.fieldId,
                parentOptionId: parentOption.optionId,
              });
            }
            return parentOptionElement;
          }
        }

        const parentFieldElement = document.querySelector(
          `[data-field-id="${parentOption.fieldId}"]`,
        );
        if (parentFieldElement) {
          if (debugContext) {
            logAiReviewNavigation('Resolved parent field wrapper.', {
              debugContext,
              fieldId: normalizedFieldId,
              parentFieldId: parentOption.fieldId,
              parentOptionId: parentOption.optionId || null,
            });
          }
          return parentFieldElement;
        }
      }

      if (debugContext) {
        logAiReviewNavigation('Failed to resolve any navigable element.', {
          debugContext,
          fieldId: normalizedFieldId,
          optionId: optionId || null,
        });
      }

      return null;
    },
    [findParentOptionForField, formJson, logAiReviewNavigation],
  );

  const focusNavigableElement = useCallback((element) => {
    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.classList.add('dbq-field-missing-highlight');
    setTimeout(
      () => element.classList.remove('dbq-field-missing-highlight'),
      2000,
    );

    const focusableElement =
      element.querySelector('input, textarea, select, button') || element;
    if (focusableElement && typeof focusableElement.focus === 'function') {
      focusableElement.focus();
    }
  }, []);

  // Handle "Go to Field" navigation from newly required modal
  const handleGoToRequiredField = useCallback(
    (field) => {
      setShowNewlyRequiredModal(false);

      setTimeout(() => {
        const element = findNavigableElementForField({
          fieldId: field.fieldId,
          optionId: field.optionId,
        });
        focusNavigableElement(element);
      }, 100);
    },
    [findNavigableElementForField, focusNavigableElement],
  );

  // Build render with group map
  const renderWithGroupMap = useMemo(
    () => (formJson ? buildRenderWithGroupMap(formJson) : {}),
    [formJson, buildRenderWithGroupMap],
  );

  const evidenceReviewVisibilityWarningMessage = useMemo(() => {
    if (!evidenceReviewGenerationData) return null;
    if (evidenceReviewGenerationData.visibilityAlertMessage) {
      return evidenceReviewGenerationData.visibilityAlertMessage;
    }

    const resolvedFieldId = String(
      evidenceReviewGenerationData.resolvedFieldId || '',
    ).trim();
    if (!resolvedFieldId || !formJson?.sections?.length) return null;

    const evidenceReviewField = findDbqFieldById(formJson, resolvedFieldId);
    if (!evidenceReviewField) return null;

    if (!isVisible(evidenceReviewField.visible_if, [])) {
      return 'Evidence Review draft exists, but the DBQ template is currently hiding the Evidence Review text box. Select "Records reviewed" to reveal it.';
    }

    for (const section of formJson.sections) {
      const parentOption = findParentOptionForField(
        resolvedFieldId,
        section.elements,
      );
      if (!parentOption?.fieldId || !parentOption?.optionId) continue;

      const parentField = findDbqFieldById(formJson, parentOption.fieldId);
      if (!parentField) continue;

      const parentValue = getFieldValue(parentOption.fieldId, []);
      const fieldIsVisible = doesParentOptionSelectionRevealField({
        parentField,
        optionId: parentOption.optionId,
        fieldValue: parentValue,
      });

      if (!fieldIsVisible) {
        const matchingOption = (parentField.options || []).find(
          (option) => option?.id === parentOption.optionId,
        );
        const optionLabel = matchingOption?.label || 'Records reviewed';
        return `Evidence Review draft exists, but the DBQ template is currently hiding the Evidence Review text box. Select "${optionLabel}" to reveal it.`;
      }

      break;
    }

    return null;
  }, [
    evidenceReviewGenerationData,
    findParentOptionForField,
    formJson,
    getFieldValue,
    isVisible,
  ]);

  const getFieldLabelByKey = useCallback(
    (fieldId) => {
      const supplementalField =
        getMusculoskeletalSupplementalFieldDefinition(fieldId);
      if (supplementalField?.label) {
        return supplementalField.label;
      }

      const field = findDbqFieldById(formJson, fieldId);
      return (
        field?.label ||
        (fieldId.includes('__') ? fieldId.split('__').pop() : fieldId)
      );
    },
    [formJson],
  );

  const inferVisibilityControllerFieldId = useCallback(
    (fieldId) => {
      const normalizedFieldId = String(fieldId || '').trim();
      if (!normalizedFieldId || !formJson?.sections?.length) return null;

      for (const section of formJson.sections) {
        const parentOption = findParentOptionForField(
          normalizedFieldId,
          section.elements,
        );
        if (parentOption?.fieldId) {
          return String(parentOption.fieldId).trim();
        }
      }

      const field = findDbqFieldById(formJson, normalizedFieldId);
      if (!field?.visible_if) return null;

      const flattened = flattenVisibleIfConditions(field.visible_if);
      if (flattened.isComplex) return null;

      const controllerFieldIds = flattened.conditions
        .map((condition) => String(condition?.field_id || '').trim())
        .filter(Boolean);

      return controllerFieldIds.length === 1 ? controllerFieldIds[0] : null;
    },
    [findParentOptionForField, formJson],
  );

  const showReviewPanel = activeReviewFieldId !== null;
  const activeComments = activeReviewFieldId
    ? getFieldComments(activeReviewFieldId)
    : [];
  const activeReviewField = useMemo(
    () =>
      findDbqFieldById(formJson, activeReviewFieldId) ||
      getMusculoskeletalSupplementalFieldDefinition(activeReviewFieldId),
    [formJson, activeReviewFieldId],
  );
  const activeReviewCurrentValue = useMemo(() => {
    if (!activeReviewFieldId) return undefined;
    if (isMusculoskeletalSupplementalFieldId(activeReviewFieldId)) {
      return getMusculoskeletalSupplementalFieldValue(
        musculoskeletalSupplementalData,
        activeReviewFieldId,
      );
    }
    return formData[activeReviewFieldId];
  }, [formData, activeReviewFieldId, musculoskeletalSupplementalData]);

  const commentFieldIds = useMemo(
    () => getAllActiveCommentFieldIds(formJson),
    [getAllActiveCommentFieldIds, formJson],
  );
  const suggestionFieldIdsFromForm = useMemo(
    () => getAllSuggestionFieldIds(formJson),
    [getAllSuggestionFieldIds, formJson],
  );
  const reviewFieldIds = useMemo(
    () => getAllReviewFieldIds(formJson),
    [formJson, getAllReviewFieldIds],
  );

  // Count comments only for fields that exist in the rendered schema (signature section is hidden here)
  const totalCommentCount = useMemo(() => {
    if (!commentFieldIds?.length) return 0;
    if (typeof getFieldCommentCount !== 'function')
      return commentFieldIds.length;
    return commentFieldIds.reduce(
      (sum, fieldId) => sum + (getFieldCommentCount(fieldId) || 0),
      0,
    );
  }, [commentFieldIds, getFieldCommentCount]);
  const aiSuggestionNoticesByFieldId = useMemo(() => {
    const noticesByFieldId = {};
    const seenNoticeKeys = new Set();

    const appendNotice = (notice) => {
      if (!notice?.fieldId) return;
      const dedupeKey = `${notice.fieldId}:${notice.aiNotice?.draftType || 'unknown'}:${notice.aiNotice?.tone || 'info'}:${notice.summary}`;
      if (seenNoticeKeys.has(dedupeKey)) return;
      seenNoticeKeys.add(dedupeKey);
      if (!noticesByFieldId[notice.fieldId]) {
        noticesByFieldId[notice.fieldId] = [];
      }
      noticesByFieldId[notice.fieldId].push(notice);
    };

    const hasDraftSuggestion = (fieldId, draftType) => {
      return getFieldSuggestions(fieldId).some(
        (suggestion) =>
          suggestion?.aiDraft?.draftType === draftType &&
          [
            SUGGESTION_STATUS_FILTERS.pending,
            SUGGESTION_STATUS_FILTERS.accepted,
          ].includes(
            String(
              suggestion?.status || SUGGESTION_STATUS_FILTERS.pending,
            ).toLowerCase(),
          ),
      );
    };

    Object.entries(aiFieldGenerationBucket).forEach(([draftType, value]) => {
      const state = value && typeof value === 'object' ? value : null;
      if (!state) return;

      const targetFieldIds = getAiGenerationNoticeTargetFieldIds({
        state,
        draftType,
        formJson,
      });
      if (targetFieldIds.length === 0) return;

      const draftLabel = AI_DRAFT_LABELS[draftType] || draftType;
      const createdAt =
        state.completedAt ||
        state.failedAt ||
        state.startedAt ||
        state.requestedAt ||
        new Date().toISOString();
      const stateStatus = String(state.status || '')
        .trim()
        .toLowerCase();

      targetFieldIds.forEach((fieldId) => {
        const hasSuggestion = hasDraftSuggestion(fieldId, draftType);
        const navigateToFieldId =
          String(state.visibilityControllerFieldId || '').trim() ||
          inferVisibilityControllerFieldId(fieldId);

        if (stateStatus === 'failed') {
          appendNotice(
            buildAiReviewNotice({
              fieldId,
              draftType,
              title: 'AI response was unable to be produced.',
              message: `${draftLabel} AI could not generate a response for this field.`,
              detail: state.errorMessage || null,
              createdAt,
              tone: 'warning',
              navigateToFieldId,
            }),
          );
          return;
        }

        if (isActiveDbqAiGenerationStatus(stateStatus) && !hasSuggestion) {
          appendNotice(
            buildAiReviewNotice({
              fieldId,
              draftType,
              title: 'AI response is being generated.',
              message: `${draftLabel} AI is currently generating a response for this field.`,
              detail: null,
              createdAt,
              tone: 'progress',
              navigateToFieldId,
            }),
          );
        }

        if (stateStatus === 'needs_attention' && !hasSuggestion) {
          appendNotice(
            buildAiReviewNotice({
              fieldId,
              draftType,
              title: 'AI response was unable to be produced.',
              message:
                state.attentionMessage ||
                `${draftLabel} AI could not generate a grounded response for this field.`,
              detail: null,
              createdAt,
              tone: 'warning',
              navigateToFieldId,
            }),
          );
        }

        if (state.infoMessage && !hasSuggestion) {
          appendNotice(
            buildAiReviewNotice({
              fieldId,
              draftType,
              title: 'No AI suggestion was added.',
              message: state.infoMessage,
              detail: null,
              createdAt,
              tone: 'info',
              navigateToFieldId,
            }),
          );
        }

        if (state.visibilityAlertMessage) {
          appendNotice(
            buildAiReviewNotice({
              fieldId,
              draftType,
              title: 'AI response needs visibility setup.',
              message: state.visibilityAlertMessage,
              detail: null,
              createdAt,
              tone: 'warning',
              navigateToFieldId,
            }),
          );
        }
      });
    });

    if (
      remarksFieldId &&
      moGenerationStatus === 'failed' &&
      autoMedicalOpinionData?.errorMessage
    ) {
      appendNotice(
        buildAiReviewNotice({
          fieldId: remarksFieldId,
          draftType: 'medical_opinion',
          title: 'AI response was unable to be produced.',
          message:
            'Medical Opinion AI could not generate a response for this field.',
          detail: autoMedicalOpinionData.errorMessage,
          createdAt:
            autoMedicalOpinionData.updatedAt ||
            autoMedicalOpinionData.requestedAt ||
            new Date().toISOString(),
          tone: 'warning',
        }),
      );
    }

    if (
      remarksFieldId &&
      isMoGenerationActive &&
      !hasDraftSuggestion(remarksFieldId, 'medical_opinion')
    ) {
      appendNotice(
        buildAiReviewNotice({
          fieldId: remarksFieldId,
          draftType: 'medical_opinion',
          title: 'AI response is being generated.',
          message: moGenerationProgressText
            ? `Medical Opinion AI is generating a response for this field. Progress: ${moGenerationProgressText}.`
            : 'Medical Opinion AI is currently generating a response for this field.',
          detail: null,
          createdAt:
            autoMedicalOpinionData?.requestedAt || new Date().toISOString(),
          tone: 'progress',
        }),
      );
    }

    if (
      evidenceReviewVisibilityWarningMessage &&
      evidenceReviewGenerationData?.resolvedFieldId
    ) {
      appendNotice(
        buildAiReviewNotice({
          fieldId: evidenceReviewGenerationData.resolvedFieldId,
          draftType: 'evidence_review',
          title: 'AI response needs visibility setup.',
          message: evidenceReviewVisibilityWarningMessage,
          detail: null,
          createdAt:
            evidenceReviewGenerationData.completedAt ||
            evidenceReviewGenerationData.requestedAt ||
            new Date().toISOString(),
          tone: 'warning',
          navigateToFieldId:
            evidenceReviewGenerationData.visibilityControllerFieldId || null,
        }),
      );
    }

    return noticesByFieldId;
  }, [
    aiFieldGenerationBucket,
    autoMedicalOpinionData?.errorMessage,
    autoMedicalOpinionData?.requestedAt,
    autoMedicalOpinionData?.updatedAt,
    evidenceReviewGenerationData?.completedAt,
    evidenceReviewGenerationData?.requestedAt,
    evidenceReviewGenerationData?.resolvedFieldId,
    evidenceReviewGenerationData?.visibilityControllerFieldId,
    evidenceReviewVisibilityWarningMessage,
    formJson,
    inferVisibilityControllerFieldId,
    getFieldSuggestions,
    isMoGenerationActive,
    moGenerationProgressText,
    moGenerationStatus,
    remarksFieldId,
  ]);
  const scrollToField = useCallback(
    (fieldId) => {
      setTimeout(() => {
        const noticesForField = aiSuggestionNoticesByFieldId[fieldId] || [];
        const navigateToFieldId = noticesForField
          .map((notice) =>
            String(notice?.aiNotice?.navigateToFieldId || '').trim(),
          )
          .find(Boolean);

        logAiReviewNavigation('Attempting review navigation.', {
          fieldId,
          noticeCount: noticesForField.length,
          notices: noticesForField.map((notice) => ({
            draftType: notice?.aiNotice?.draftType || null,
            tone: notice?.aiNotice?.tone || null,
            title: notice?.aiNotice?.title || notice?.summary || null,
            navigateToFieldId: notice?.aiNotice?.navigateToFieldId || null,
          })),
        });

        let fieldElement = findNavigableElementForField({
          fieldId,
          debugContext: 'primary_field_lookup',
        });

        if (!fieldElement) {
          if (navigateToFieldId && navigateToFieldId !== fieldId) {
            fieldElement = findNavigableElementForField({
              fieldId: navigateToFieldId,
              debugContext: 'fallback_controller_lookup',
            });
          }
        }

        logAiReviewNavigation('Navigation lookup completed.', {
          fieldId,
          navigateToFieldId: navigateToFieldId || null,
          elementFound: Boolean(fieldElement),
        });

        focusNavigableElement(fieldElement);
      }, 100);
    },
    [
      aiSuggestionNoticesByFieldId,
      findNavigableElementForField,
      focusNavigableElement,
      logAiReviewNavigation,
    ],
  );
  const getReviewSuggestions = useCallback(
    (fieldId) => {
      if (!fieldId) return [];
      return [
        ...(aiSuggestionNoticesByFieldId[fieldId] || []),
        ...getFieldSuggestions(fieldId),
      ];
    },
    [aiSuggestionNoticesByFieldId, getFieldSuggestions],
  );
  const suggestionFieldIds = useMemo(() => {
    const mergedFieldIds = [...suggestionFieldIdsFromForm];
    Object.keys(aiSuggestionNoticesByFieldId).forEach((fieldId) => {
      if (fieldId && !mergedFieldIds.includes(fieldId)) {
        mergedFieldIds.push(fieldId);
      }
    });
    return mergedFieldIds;
  }, [aiSuggestionNoticesByFieldId, suggestionFieldIdsFromForm]);
  const isProviderPendingSuggestionView = isProvider;
  const getSidebarReviewSuggestions = useCallback(
    (fieldId) => {
      const fieldSuggestions = getReviewSuggestions(fieldId);
      if (!isProviderPendingSuggestionView) return fieldSuggestions;
      return fieldSuggestions.filter(
        (suggestion) => !isInlineAiNotice(suggestion),
      );
    },
    [getReviewSuggestions, isProviderPendingSuggestionView],
  );
  const totalSuggestionStats = useMemo(
    () =>
      buildSuggestionStatsFromItems(
        suggestionFieldIds.flatMap((fieldId) =>
          getSidebarReviewSuggestions(fieldId),
        ),
      ),
    [getSidebarReviewSuggestions, suggestionFieldIds],
  );
  const totalSuggestionCount = totalSuggestionStats.total;
  const pendingSuggestionCount = totalSuggestionStats.pending || 0;
  const effectiveSuggestionStatusFilter = isProviderPendingSuggestionView
    ? SUGGESTION_STATUS_FILTERS.pending
    : activeSuggestionStatusFilter;
  const getSuggestionFieldIdsForFilter = useCallback(
    (statusFilter = SUGGESTION_STATUS_FILTERS.pending) => {
      return suggestionFieldIds.filter((fieldId) => {
        const fieldSuggestions = getSidebarReviewSuggestions(fieldId);

        return fieldSuggestions.some(
          (suggestion) =>
            String(suggestion?.status || '').toLowerCase() === statusFilter,
        );
      });
    },
    [getSidebarReviewSuggestions, suggestionFieldIds],
  );
  const filteredSuggestionFieldIds = useMemo(
    () => getSuggestionFieldIdsForFilter(effectiveSuggestionStatusFilter),
    [effectiveSuggestionStatusFilter, getSuggestionFieldIdsForFilter],
  );
  const candidateInlineSuggestionNavigatorFieldIds = useMemo(
    () =>
      suggestionFieldIds.filter((fieldId) =>
        getReviewSuggestions(fieldId).some((suggestion) => {
          const status = String(
            suggestion?.status || SUGGESTION_STATUS_FILTERS.pending,
          ).toLowerCase();
          return INLINE_SUGGESTION_NAVIGATION_STATUSES.has(status);
        }),
      ),
    [getReviewSuggestions, suggestionFieldIds],
  );
  useEffect(() => {
    if (!isInlineSuggestionsView) {
      setOrderedInlineSuggestionNavigatorFieldIds((currentFieldIds) =>
        currentFieldIds.length > 0 ? [] : currentFieldIds,
      );
      return;
    }

    const orderedFieldIds = orderFieldIdsByRenderedPosition(
      candidateInlineSuggestionNavigatorFieldIds,
      (fieldId) => findNavigableElementForField({ fieldId }),
    );
    setOrderedInlineSuggestionNavigatorFieldIds((currentFieldIds) =>
      areOrderedFieldIdsEqual(currentFieldIds, orderedFieldIds)
        ? currentFieldIds
        : orderedFieldIds,
    );
  }, [
    candidateInlineSuggestionNavigatorFieldIds,
    findNavigableElementForField,
    inlineSuggestionRevision,
    isInlineSuggestionsView,
  ]);
  const inlineSuggestionNavigatorFieldIds = isInlineSuggestionsView
    ? orderedInlineSuggestionNavigatorFieldIds
    : candidateInlineSuggestionNavigatorFieldIds;
  const inlineSuggestionNavigatorCount =
    inlineSuggestionNavigatorFieldIds.length;
  const activeInlineSuggestionNavigatorIndex = useMemo(() => {
    if (inlineSuggestionNavigatorCount === 0) return -1;
    const currentIndex = inlineSuggestionNavigatorFieldIds.indexOf(
      activeInlineSuggestionNavigatorFieldId,
    );
    return currentIndex;
  }, [
    activeInlineSuggestionNavigatorFieldId,
    inlineSuggestionNavigatorCount,
    inlineSuggestionNavigatorFieldIds,
  ]);
  const activeInlineSuggestionNavigatorFieldIdResolved =
    activeInlineSuggestionNavigatorIndex >= 0
      ? inlineSuggestionNavigatorFieldIds[activeInlineSuggestionNavigatorIndex]
      : null;
  const activeInlineSuggestionNavigatorLabel =
    activeInlineSuggestionNavigatorFieldIdResolved
      ? getFieldLabelByKey(activeInlineSuggestionNavigatorFieldIdResolved)
      : 'No suggestions in form';
  const availableSuggestionStatusFilters = useMemo(
    () =>
      SUGGESTION_STATUS_FILTER_ORDER.filter((statusFilter) => {
        return (totalSuggestionStats?.[statusFilter] || 0) > 0;
      }),
    [totalSuggestionStats],
  );
  const defaultSuggestionStatusFilter = isProviderPendingSuggestionView
    ? SUGGESTION_STATUS_FILTERS.pending
    : totalSuggestionStats.pending > 0
      ? SUGGESTION_STATUS_FILTERS.pending
      : availableSuggestionStatusFilters[0] ||
        SUGGESTION_STATUS_FILTERS.pending;
  const isSuggestionCountLoading = hasActiveDbqAiGeneration(userDbq?.formData);
  const displayedSuggestionCount = isProviderPendingSuggestionView
    ? pendingSuggestionCount
    : useInlineSuggestionMode
      ? 0
      : totalSuggestionCount;
  const shouldShowSuggestionNotificationCount =
    !useInlineSuggestionMode &&
    (displayedSuggestionCount > 0 || isSuggestionCountLoading);
  const canAccessSuggestions =
    !useInlineSuggestionMode &&
    (totalSuggestionCount > 0 ||
      canSuggest ||
      canAcceptSuggestions ||
      canDismissSuggestions);
  const activeCommentCount = activeReviewFieldId
    ? getFieldCommentCount(activeReviewFieldId)
    : 0;
  const activeSuggestions = useMemo(
    () =>
      activeReviewFieldId
        ? getSidebarReviewSuggestions(activeReviewFieldId)
        : [],
    [activeReviewFieldId, getSidebarReviewSuggestions],
  );
  const activeSuggestionStats = useMemo(
    () =>
      activeReviewFieldId
        ? buildSuggestionStatsFromItems(
            getSidebarReviewSuggestions(activeReviewFieldId),
          )
        : EMPTY_SUGGESTION_STATS,
    [activeReviewFieldId, getSidebarReviewSuggestions],
  );
  const activeSuggestionCount = isProviderPendingSuggestionView
    ? activeSuggestionStats.pending || 0
    : useInlineSuggestionMode
      ? activeReviewTab === 'suggestions'
        ? activeSuggestionStats[effectiveSuggestionStatusFilter] ||
          activeSuggestionStats.total ||
          0
        : 0
      : activeSuggestionStats.total;
  const getReviewItemCount = useCallback(
    (fieldId) => {
      if (!fieldId) return 0;
      return (
        (getFieldCommentCount(fieldId) || 0) +
        (useInlineSuggestionMode
          ? 0
          : getSidebarReviewSuggestions(fieldId).length)
      );
    },
    [
      getFieldCommentCount,
      getSidebarReviewSuggestions,
      useInlineSuggestionMode,
    ],
  );

  const getPreferredReviewTab = useCallback(
    (fieldId, requestedTab = null) => {
      // In inline-suggestion mode the form IS the suggestion editor — the
      // side panel only shows comments. Force the tab so the auto-close
      // effect (which closes the panel when it lands on 'suggestions' in
      // inline mode) doesn't fire.
      if (useInlineSuggestionMode) {
        return 'comments';
      }

      const fieldCommentCount = fieldId
        ? getFieldCommentCount(fieldId) || 0
        : 0;
      const fieldSuggestionStats = fieldId
        ? buildSuggestionStatsFromItems(getSidebarReviewSuggestions(fieldId))
        : EMPTY_SUGGESTION_STATS;
      const fieldSuggestionCount = isProviderPendingSuggestionView
        ? fieldSuggestionStats?.pending || 0
        : fieldSuggestionStats?.total || 0;

      if (
        requestedTab === 'comments' &&
        (fieldCommentCount > 0 || canComment || !canAccessSuggestions)
      ) {
        return 'comments';
      }

      if (
        requestedTab === 'suggestions' &&
        (fieldSuggestionCount > 0 || canAccessSuggestions)
      ) {
        return 'suggestions';
      }

      if (fieldSuggestionCount > 0 && fieldCommentCount === 0) {
        return 'suggestions';
      }

      if (fieldCommentCount > 0 && fieldSuggestionCount === 0) {
        return 'comments';
      }

      if (fieldSuggestionCount > 0 && fieldCommentCount > 0) {
        return canAccessSuggestions ? 'suggestions' : 'comments';
      }

      if (canAccessSuggestions) {
        return 'suggestions';
      }

      return 'comments';
    },
    [
      canAccessSuggestions,
      canComment,
      getFieldCommentCount,
      getSidebarReviewSuggestions,
      isProviderPendingSuggestionView,
      useInlineSuggestionMode,
    ],
  );

  const openReviewPanel = useCallback(
    (fieldId, fieldLabel, requestedTab = null, options = {}) => {
      setActiveReviewFieldId(fieldId);
      setActiveReviewFieldLabel(fieldLabel || getFieldLabelByKey(fieldId));
      setActiveReviewTab(getPreferredReviewTab(fieldId, requestedTab));
      if (options.scroll !== false) {
        scrollToField(fieldId);
      }
    },
    [getFieldLabelByKey, getPreferredReviewTab, scrollToField],
  );

  // The panel header names the side too ("Left TMJ › Comments:", Nic
  // 2026-11-04): side-by-side groups reuse labels, so a field change shows.
  const activeReviewHeaderLabel = useMemo(() => {
    const groupLabel = activeReviewFieldId
      ? findDbqFieldGroupLabel(formJson, activeReviewFieldId)
      : null;
    if (!groupLabel || groupLabel === activeReviewFieldLabel) return activeReviewFieldLabel;
    return `${groupLabel} › ${activeReviewFieldLabel}`;
  }, [activeReviewFieldId, activeReviewFieldLabel, formJson]);

  const openSuggestionComposerPanel = useCallback(
    (fieldId, fieldLabel) => {
      if (!fieldId) return;
      setPendingSuggestionComposerFieldId(fieldId);
      setActiveSuggestionStatusFilter(SUGGESTION_STATUS_FILTERS.pending);
      openReviewPanel(fieldId, fieldLabel, 'suggestions');
    },
    [openReviewPanel],
  );

  const handleOpenDismissedSuggestions = useCallback(() => {
    const firstDismissedFieldId = getSuggestionFieldIdsForFilter(
      SUGGESTION_STATUS_FILTERS.dismissed,
    )[0];

    if (!firstDismissedFieldId) return;

    setActiveSuggestionStatusFilter(SUGGESTION_STATUS_FILTERS.dismissed);
    openReviewPanel(
      firstDismissedFieldId,
      getFieldLabelByKey(firstDismissedFieldId),
      'suggestions',
    );
  }, [getFieldLabelByKey, getSuggestionFieldIdsForFilter, openReviewPanel]);

  useEffect(() => {
    if (!isInlineSuggestionsView || inlineSuggestionNavigatorCount === 0) {
      previousInlineSuggestionNavigatorFieldIdsRef.current = [];
      hasManuallyNavigatedInlineSuggestionNavigatorRef.current = false;
      if (activeInlineSuggestionNavigatorFieldId !== null) {
        setActiveInlineSuggestionNavigatorFieldId(null);
      }
      return;
    }

    const previousFieldIds =
      previousInlineSuggestionNavigatorFieldIdsRef.current;
    const orderChanged = !areOrderedFieldIdsEqual(
      previousFieldIds,
      inlineSuggestionNavigatorFieldIds,
    );
    const shouldClearSelection =
      (orderChanged &&
        (previousFieldIds.length === 0 ||
          !hasManuallyNavigatedInlineSuggestionNavigatorRef.current)) ||
      (activeInlineSuggestionNavigatorFieldId &&
        !inlineSuggestionNavigatorFieldIds.includes(
          activeInlineSuggestionNavigatorFieldId,
        ));

    previousInlineSuggestionNavigatorFieldIdsRef.current =
      inlineSuggestionNavigatorFieldIds;

    if (
      shouldClearSelection &&
      activeInlineSuggestionNavigatorFieldId !== null
    ) {
      setActiveInlineSuggestionNavigatorFieldId(null);
    }
  }, [
    activeInlineSuggestionNavigatorFieldId,
    inlineSuggestionNavigatorCount,
    inlineSuggestionNavigatorFieldIds,
    isInlineSuggestionsView,
  ]);

  const handleInlineSuggestionNavigatorMove = useCallback(
    (direction) => {
      if (!isInlineSuggestionsView || inlineSuggestionNavigatorCount === 0)
        return;

      const nextIndex =
        activeInlineSuggestionNavigatorIndex >= 0
          ? (activeInlineSuggestionNavigatorIndex +
              (direction === 'previous' ? -1 : 1) +
              inlineSuggestionNavigatorCount) %
            inlineSuggestionNavigatorCount
          : direction === 'previous'
            ? inlineSuggestionNavigatorCount - 1
            : 0;
      const nextFieldId = inlineSuggestionNavigatorFieldIds[nextIndex];

      hasManuallyNavigatedInlineSuggestionNavigatorRef.current = true;
      setActiveInlineSuggestionNavigatorFieldId(nextFieldId);
      scrollToField(nextFieldId);
    },
    [
      activeInlineSuggestionNavigatorIndex,
      inlineSuggestionNavigatorCount,
      inlineSuggestionNavigatorFieldIds,
      isInlineSuggestionsView,
      scrollToField,
    ],
  );

  const handleInlineSuggestionNavigatorCurrent = useCallback(() => {
    if (!activeInlineSuggestionNavigatorFieldIdResolved) return;
    scrollToField(activeInlineSuggestionNavigatorFieldIdResolved);
  }, [activeInlineSuggestionNavigatorFieldIdResolved, scrollToField]);

  const handleReviewNavigate = useCallback(
    (fieldId) => {
      setActiveReviewFieldId(fieldId);
      setActiveReviewFieldLabel(getFieldLabelByKey(fieldId));
      setActiveReviewTab((currentTab) =>
        getPreferredReviewTab(fieldId, currentTab),
      );
      scrollToField(fieldId);
    },
    [getFieldLabelByKey, getPreferredReviewTab, scrollToField],
  );

  const queueSuggestionReviewNavigation = useCallback(() => {
    if (
      activeReviewTab !== 'suggestions' ||
      effectiveSuggestionStatusFilter !== SUGGESTION_STATUS_FILTERS.pending ||
      !activeReviewFieldId
    ) {
      pendingSuggestionReviewNavigationRef.current = null;
      return;
    }

    pendingSuggestionReviewNavigationRef.current = {
      fieldId: activeReviewFieldId,
      previousFieldIds: filteredSuggestionFieldIds,
    };
  }, [
    activeReviewFieldId,
    activeReviewTab,
    effectiveSuggestionStatusFilter,
    filteredSuggestionFieldIds,
  ]);

  const handleReviewClose = useCallback(() => {
    pendingSuggestionReviewNavigationRef.current = null;
    setPendingSuggestionComposerFieldId(null);
    setActiveReviewFieldId(null);
    setActiveReviewFieldLabel('');
    setActiveReviewTab('comments');
  }, []);

  useEffect(() => {
    if (!pendingSuggestionComposerFieldId) return;
    if (
      activeReviewTab === 'suggestions' &&
      activeReviewFieldId === pendingSuggestionComposerFieldId
    ) {
      return;
    }

    setPendingSuggestionComposerFieldId(null);
  }, [activeReviewFieldId, activeReviewTab, pendingSuggestionComposerFieldId]);

  useEffect(() => {
    if (isInlineSuggestionsView && activeReviewTab === 'suggestions') {
      handleReviewClose();
    }
  }, [activeReviewTab, handleReviewClose, isInlineSuggestionsView]);

  const handleSuggestionStatusFilterChange = useCallback(
    (statusFilter) => {
      if (isProviderPendingSuggestionView) {
        setActiveSuggestionStatusFilter(SUGGESTION_STATUS_FILTERS.pending);
        return;
      }

      setActiveSuggestionStatusFilter(statusFilter);

      const nextFieldIds = getSuggestionFieldIdsForFilter(statusFilter);
      if (nextFieldIds.length === 0) return;
      if (nextFieldIds.includes(activeReviewFieldId)) return;

      const firstFieldId = nextFieldIds[0];
      openReviewPanel(
        firstFieldId,
        getFieldLabelByKey(firstFieldId),
        'suggestions',
      );
    },
    [
      activeReviewFieldId,
      getFieldLabelByKey,
      getSuggestionFieldIdsForFilter,
      isProviderPendingSuggestionView,
      openReviewPanel,
    ],
  );

  const hasMoEvidence = Boolean(editableAutoMedicalOpinionText);

  // Medical Opinion is surfaced through the same suggestion artifact controls as other AI fields.
  const canShowMoActionsForField = useCallback(() => false, []);

  const handleShowMoEvidence = useCallback(() => {
    setShowMoEvidenceModal(true);
  }, []);
  const handleOpenRemarksEditorModal = useCallback(() => {
    if (!isManagedMedicalOpinionRemarks) return;
    setRemarksEditorSubmitMode(null);
    setRemarksEditorText(editableAutoMedicalOpinionText);
    setShowRemarksEditorModal(true);
  }, [editableAutoMedicalOpinionText, isManagedMedicalOpinionRemarks]);

  const handleSaveRemarksEditor = useCallback(() => {
    if (!canManageMoRemarks) return;

    const nextValue = String(remarksEditorText || '').trim();
    if (!nextValue) {
      toast.error('Medical Opinion text cannot be empty.');
      return;
    }

    setRemarksEditorSubmitMode('save');
    saveRemarksFullTextMutation.mutate({
      fullMedicalOpinionText: nextValue,
      action: 'save',
    });
  }, [canManageMoRemarks, remarksEditorText, saveRemarksFullTextMutation]);

  const handleOpenMoGenerationModal = useCallback(() => {
    setMoGenerationPromptInput('');
    setShowMoGenerationModal(true);
  }, []);

  const handleRequestMoGeneration = useCallback(async () => {
    if (!canManageMedicalOpinion || !userDbqId) return;

    if (moRequestContentions.length === 0) {
      toast.error(
        'Please assign at least one contention in the request workspace before generating a Medical Opinion.',
      );
      return;
    }

    if (hasMissingMoToe) {
      toast.error(
        'Each request contention needs a Theory of Entitlement (TOE) before generating a Medical Opinion.',
      );
      return;
    }

    requestMoGenerationMutation.mutate({
      userDbqId,
      prompt: moGenerationPromptInput,
    });
  }, [
    canManageMedicalOpinion,
    hasMissingMoToe,
    moRequestContentions.length,
    userDbqId,
    requestMoGenerationMutation,
    moGenerationPromptInput,
  ]);

  const getCanonicalFieldValue = useCallback(
    (fieldId, instancePath = []) => {
      if (!fieldId) return undefined;
      if (isMusculoskeletalSupplementalFieldId(fieldId)) {
        return getMusculoskeletalSupplementalFieldValue(
          formData?._supplemental?.musculoskeletal,
          fieldId,
        );
      }

      const key = buildFieldKey(fieldId, instancePath);
      return formData[key] !== undefined ? formData[key] : formData[fieldId];
    },
    [buildFieldKey, formData],
  );

  const getInlineSuggestionFieldValue = useCallback(
    (fieldId, instancePath = []) => {
      if (!fieldId) return undefined;
      const key = buildFieldKey(fieldId, instancePath);
      if (isInlineCurrentDbqView) {
        return getCanonicalFieldValue(fieldId, instancePath);
      }

      const latestAcceptedSuggestion = getLatestInlineSuggestionWithStatus(
        formData?._suggestions?.[key],
        SUGGESTION_STATUS_FILTERS.accepted,
      );
      if (latestAcceptedSuggestion) {
        const acceptedValue = getInlineSuggestionValue(
          latestAcceptedSuggestion,
        );
        if (typeof acceptedValue !== 'undefined') return acceptedValue;
      }

      if (hasOwnValue(inlineSuggestionDrafts, key)) {
        return inlineSuggestionDrafts[key];
      }
      return getCanonicalFieldValue(fieldId, instancePath);
    },
    [
      buildFieldKey,
      formData,
      getCanonicalFieldValue,
      inlineSuggestionDrafts,
      isInlineCurrentDbqView,
    ],
  );

  const handleInlineSuggestionFieldChange = useCallback(
    (fieldId, value, instancePath = []) => {
      if (!canCreateSuggestions) {
        return { newlyRequired: [] };
      }

      const key = buildFieldKey(fieldId, instancePath);
      setInlineSuggestionDrafts((currentDrafts) => ({
        ...currentDrafts,
        [key]: value,
      }));
      setInlineSuggestionDirtyFieldIds((currentDirtyFieldIds) => {
        const nextDirtyFieldIds = new Set(currentDirtyFieldIds);
        nextDirtyFieldIds.add(key);
        return nextDirtyFieldIds;
      });
      setInlineSuggestionRevision((currentRevision) => currentRevision + 1);
      return { newlyRequired: [] };
    },
    [buildFieldKey, canCreateSuggestions],
  );

  const getInlineSuggestionStateForField = useCallback(
    (field, instancePath = []) => {
      if (!useInlineSuggestionMode || !field?.id) return null;
      const key = buildFieldKey(field.id, instancePath);
      const actualValue = getCanonicalFieldValue(field.id, instancePath);
      const draftExists = hasOwnValue(inlineSuggestionDrafts, key);
      const draftValue = draftExists ? inlineSuggestionDrafts[key] : undefined;
      const hasActualValue = isDbqValuePresent(actualValue);
      const hasDraftValue = draftExists && isDbqValuePresent(draftValue);
      const fieldSuggestions = getReviewSuggestions(key);
      const rejectedSuggestions = getInlineSuggestionsWithStatus(
        fieldSuggestions,
        SUGGESTION_STATUS_FILTERS.rejected,
      );
      const hasRejectedSuggestions = rejectedSuggestions.length > 0;
      const latestAcceptedSuggestion = getLatestInlineSuggestionWithStatus(
        fieldSuggestions,
        SUGGESTION_STATUS_FILTERS.accepted,
      );
      const latestPendingSuggestion =
        getLatestPendingInlineSuggestion(fieldSuggestions);
      const inlineActionSuggestion = getInlineSuggestionActionSource({
        latestPendingSuggestion,
        latestAcceptedSuggestion,
      });
      const isAiGeneratedSuggestion = isAiGeneratedInlineSuggestion(
        inlineActionSuggestion,
      );
      const hasEvidence =
        getInlineSuggestionEvidenceRefs(inlineActionSuggestion).length > 0;
      const latestAiNotice = getLatestInlineAiNotice(fieldSuggestions);
      const hasMissingDetail =
        !isInlineCurrentDbqView &&
        inlineSuggestionHasMissingDetail(latestPendingSuggestion);

      if (latestAcceptedSuggestion) {
        return {
          enabled: true,
          fieldId: key,
          isAccepted: true,
          isLocked: true,
          isAiGenerated: isAiGeneratedSuggestion,
          hasEvidence,
          hasMissingDetail: false,
          hasRejectedSuggestions,
          rejectedSuggestionCount: rejectedSuggestions.length,
          label: 'Accepted',
          helpText: 'This suggestion was accepted and is locked for review.',
        };
      }

      if (
        latestAiNotice &&
        !latestPendingSuggestion &&
        !latestAcceptedSuggestion
      ) {
        const hasGenerationFailure =
          isInlineGenerationFailureNotice(latestAiNotice);
        const isLoading = isInlineProgressNotice(latestAiNotice);
        const noticeHelpText = isLoading
          ? latestAiNotice?.payload?.value ||
            latestAiNotice.summary ||
            'AI is currently generating a response for this field.'
          : hasGenerationFailure
            ? null
            : latestAiNotice.note ||
              latestAiNotice?.payload?.value ||
              latestAiNotice.summary ||
              'AI was unable to produce a response for this field.';
        return {
          enabled: true,
          fieldId: key,
          hasActualValue,
          hasDraftValue,
          isNotice: true,
          isLoading,
          isLocked: isLoading,
          isAiGenerated: false,
          hasEvidence: false,
          hasMissingDetail,
          hasGenerationFailure,
          hasRejectedSuggestions,
          rejectedSuggestionCount: rejectedSuggestions.length,
          label: isLoading ? 'AI loading' : 'AI unavailable',
          helpText: noticeHelpText,
        };
      }

      if (
        !hasActualValue &&
        !hasDraftValue &&
        !latestAiNotice &&
        !hasMissingDetail &&
        !hasRejectedSuggestions
      ) {
        return null;
      }

      return {
        enabled: true,
        fieldId: key,
        hasActualValue,
        hasDraftValue,
        isAiGenerated: isAiGeneratedSuggestion,
        hasEvidence,
        hasMissingDetail,
        hasRejectedSuggestions,
        rejectedSuggestionCount: rejectedSuggestions.length,
        label: hasMissingDetail ? 'Needs detail' : null,
        helpText: hasMissingDetail
          ? 'This suggestion needs additional detail before it can be accepted.'
          : null,
      };
    },
    [
      buildFieldKey,
      getCanonicalFieldValue,
      getReviewSuggestions,
      inlineSuggestionDrafts,
      isInlineCurrentDbqView,
      useInlineSuggestionMode,
    ],
  );

  const getInlineSuggestionValueForCondition = useCallback(
    (fieldId, instancePath = []) =>
      getInlineSuggestionFieldValue(fieldId, instancePath),
    [getInlineSuggestionFieldValue],
  );

  const isInlineSuggestionVisible = useCallback(
    (visibleIf, instancePath = []) => {
      if (!visibleIf) return true;
      return evaluateInlineCondition(
        visibleIf,
        (fieldId) =>
          getInlineSuggestionValueForCondition(fieldId, instancePath),
        (fieldId) => (repeatingInstances[fieldId] || []).length,
      );
    },
    [getInlineSuggestionValueForCondition, repeatingInstances],
  );

  const isInlineSuggestionConditionallyRequired = useCallback(
    (field, instancePath = []) => {
      if (!field?.required_if) return false;
      return evaluateInlineCondition(
        field.required_if,
        (fieldId) =>
          getInlineSuggestionValueForCondition(fieldId, instancePath),
        (fieldId) => (repeatingInstances[fieldId] || []).length,
      );
    },
    [getInlineSuggestionValueForCondition, repeatingInstances],
  );

  const isInlineSuggestionOptionConditionallyRequired = useCallback(
    (option, instancePath = []) => {
      if (!option?.required_if) return false;
      return evaluateInlineCondition(
        option.required_if,
        (fieldId) =>
          getInlineSuggestionValueForCondition(fieldId, instancePath),
        (fieldId) => (repeatingInstances[fieldId] || []).length,
      );
    },
    [getInlineSuggestionValueForCondition, repeatingInstances],
  );

  const handleOpenInlineSuggestionDetail = useCallback(
    (fieldId) => {
      const normalizedFieldId = String(fieldId || '').trim();
      if (!normalizedFieldId) return;

      const suggestion = getLatestPendingInlineSuggestion(
        getReviewSuggestions(normalizedFieldId),
      );
      if (!suggestion) return;

      const missingInputs = getInlineSuggestionMissingInputs(suggestion);
      setInlineMissingDetailFieldId(normalizedFieldId);
      setInlineMissingDetailSuggestion(suggestion);
      setInlineMissingDetailDraftText(
        String(suggestion?.payload?.value || suggestion?.summary || ''),
      );
      setInlineMissingInputValues(
        Object.fromEntries(
          missingInputs.map((missingInput, index) => [
            buildInlineMissingInputStateKey(missingInput, index),
            '',
          ]),
        ),
      );
      setInlineMissingInputSearches({});
    },
    [getReviewSuggestions],
  );

  const handleOpenInlineSuggestionEvidence = useCallback(
    (fieldId) => {
      const normalizedFieldId = String(fieldId || '').trim();
      if (!normalizedFieldId) return;

      const fieldSuggestions = getReviewSuggestions(normalizedFieldId);
      const latestPendingSuggestion =
        getLatestPendingInlineSuggestion(fieldSuggestions);
      const latestAcceptedSuggestion = getLatestInlineSuggestionWithStatus(
        fieldSuggestions,
        SUGGESTION_STATUS_FILTERS.accepted,
      );
      const suggestion = getInlineSuggestionActionSource({
        latestPendingSuggestion,
        latestAcceptedSuggestion,
      });

      if (!isAiGeneratedInlineSuggestion(suggestion)) return;
      setInlineEvidenceFieldId(normalizedFieldId);
      setInlineEvidenceSuggestion(suggestion);
    },
    [getReviewSuggestions],
  );

  const handleOpenInlineGenerationFailure = useCallback(
    (fieldId) => {
      const normalizedFieldId = String(fieldId || '').trim();
      if (!normalizedFieldId) return;

      const notice = getReviewSuggestions(normalizedFieldId).find(
        isInlineGenerationFailureNotice,
      );
      if (!notice) return;

      setInlineGenerationFailureFieldId(normalizedFieldId);
      setInlineGenerationFailureNotice(notice);
    },
    [getReviewSuggestions],
  );

  const handleOpenInlineRejectedSuggestions = useCallback(
    (fieldId) => {
      const normalizedFieldId = String(fieldId || '').trim();
      if (!normalizedFieldId) return;

      const rejectedSuggestions = getInlineSuggestionsWithStatus(
        getReviewSuggestions(normalizedFieldId),
        SUGGESTION_STATUS_FILTERS.rejected,
      );
      if (rejectedSuggestions.length === 0) return;

      setInlineRejectedSuggestionsFieldId(normalizedFieldId);
    },
    [getReviewSuggestions],
  );

  const closeInlineEvidenceModal = useCallback(() => {
    setInlineEvidenceSuggestion(null);
    setInlineEvidenceFieldId(null);
    setInlineEvidencePage({
      fileId: null,
      fileName: null,
      pageNumber: null,
    });
  }, []);

  const closeInlineGenerationFailureModal = useCallback(() => {
    setInlineGenerationFailureFieldId(null);
    setInlineGenerationFailureNotice(null);
  }, []);

  const closeInlineRejectedSuggestionsModal = useCallback(() => {
    setInlineRejectedSuggestionsFieldId(null);
  }, []);

  const openInlineEvidencePage = useCallback(
    ({ fileId, fileName, pageNumber }) => {
      if (!fileId || !pageNumber) return;
      setInlineEvidencePage({
        fileId,
        fileName: fileName || null,
        pageNumber,
      });
    },
    [],
  );

  const closeInlineEvidencePage = useCallback(() => {
    setInlineEvidencePage({
      fileId: null,
      fileName: null,
      pageNumber: null,
    });
  }, []);

  const inlineEvidenceRefs = useMemo(
    () => getInlineSuggestionEvidenceRefs(inlineEvidenceSuggestion),
    [inlineEvidenceSuggestion],
  );
  const inlineEvidenceTranscriptRefs = useMemo(
    () => getInlineSuggestionTranscriptRefs(inlineEvidenceSuggestion),
    [inlineEvidenceSuggestion],
  );
  const inlineRejectedSuggestions = useMemo(
    () =>
      inlineRejectedSuggestionsFieldId
        ? getInlineSuggestionsWithStatus(
            getReviewSuggestions(inlineRejectedSuggestionsFieldId),
            SUGGESTION_STATUS_FILTERS.rejected,
          )
        : [],
    [getReviewSuggestions, inlineRejectedSuggestionsFieldId],
  );

  const displayedMusculoskeletalSupplementalData = useMemo(() => {
    if (!useInlineSuggestionMode || isInlineCurrentDbqView) {
      return musculoskeletalSupplementalData;
    }

    return buildInlineMusculoskeletalSupplementalData(
      musculoskeletalSupplementalData,
      getInlineSuggestionFieldValue,
    );
  }, [
    getInlineSuggestionFieldValue,
    isInlineCurrentDbqView,
    musculoskeletalSupplementalData,
    useInlineSuggestionMode,
  ]);

  const enhancedFormContext = useMemo(
    () => ({
      ...wrappedFormContext,
      ...(isInlineSuggestionsView
        ? {
            getFieldValue: getInlineSuggestionFieldValue,
            handleFieldChange: handleInlineSuggestionFieldChange,
            isVisible: isInlineSuggestionVisible,
            isConditionallyRequired: isInlineSuggestionConditionallyRequired,
            isOptionConditionallyRequired:
              isInlineSuggestionOptionConditionallyRequired,
            getFieldSuggestionCount: () => 0,
            getInlineSuggestionStateForField,
            onOpenInlineSuggestionDetail: handleOpenInlineSuggestionDetail,
            onOpenInlineSuggestionEvidence: handleOpenInlineSuggestionEvidence,
            onOpenInlineGenerationFailure: handleOpenInlineGenerationFailure,
            onOpenInlineRejectedSuggestions:
              handleOpenInlineRejectedSuggestions,
          }
        : {}),
      useInlineSuggestionMode,
      finalizedLocked,
      // Reverts the managed MO remarks field to the signed full opinion via
      // the MO save endpoint (direct field writes are server-blocked for it).
      revertManagedMoRemarks: (signedFullText) =>
        saveRemarksFullTextMutation.mutate({
          fullMedicalOpinionText: signedFullText,
        }),
      // finalizedLocked included: finalizing while the editor is open flips
      // every field to disabled, and the transition-clear effect would wipe
      // values client-side (the server would 403 the save, but the screen
      // would still empty out).
      suppressDisabledFieldValueClear:
        useInlineSuggestionMode ||
        isCaseManagerProviderReviewLocked ||
        finalizedLocked,
      canShowEvidenceForField: canShowMoActionsForField,
      onShowEvidence: handleShowMoEvidence,
      canGenerateMoForField: canShowMoActionsForField,
      onGenerateMo: handleOpenMoGenerationModal,
      moGenerateButtonLabel: isMoGenerationActive
        ? 'Generating MO...'
        : hasMoEvidence
          ? 'Regenerate MO'
          : 'Generate MO',
      showExamDatePlaceholderIfEmpty: isCaseManagerActor,
      isMoGenerateButtonDisabled:
        isMoGenerationActive || requestMoGenerationMutation.isPending,
      moEvidenceButtonLabel: isMoGenerationActive
        ? 'Generating Evidence...'
        : 'Show Evidence',
      isMoEvidenceButtonDisabled: isMoGenerationActive && !hasMoEvidence,
      getManagedMedicalOpinionRemarksState: (field) => {
        if (
          !isManagedMedicalOpinionRemarks ||
          !remarksFieldId ||
          field?.id !== remarksFieldId
        ) {
          return null;
        }

        return {
          isManaged: true,
          isTruncated: isManagedMedicalOpinionTruncated,
          canEdit: canManageMoRemarks,
          buttonLabel: canManageMoRemarks
            ? activeMoDraftSuggestionStatus === 'pending'
              ? 'Edit Full Medical Opinion Draft'
              : 'Edit Full Medical Opinion'
            : 'View Full Medical Opinion',
          helpText: canManageMoRemarks
            ? activeMoDraftSuggestionStatus === 'pending'
              ? 'This field is waiting on AI draft review. Edit the full Medical Opinion draft here; accepting the suggestion will apply the remarks-safe version.'
              : undefined
            : isManagedMedicalOpinionTruncated
              ? 'This field shows the DBQ-safe truncated version. View the full Medical Opinion separately.'
              : 'This remarks field is managed through the full Medical Opinion viewer.',
        };
      },
      openManagedMedicalOpinionRemarksEditor: (field) => {
        if (!remarksFieldId || field?.id !== remarksFieldId) return;
        handleOpenRemarksEditorModal();
      },
    }),
    [
      wrappedFormContext,
      finalizedLocked,
      saveRemarksFullTextMutation,
      canShowMoActionsForField,
      handleShowMoEvidence,
      handleOpenRemarksEditorModal,
      handleOpenMoGenerationModal,
      hasMoEvidence,
      canManageMoRemarks,
      isManagedMedicalOpinionRemarks,
      isManagedMedicalOpinionTruncated,
      activeMoDraftSuggestionStatus,
      remarksFieldId,
      requestMoGenerationMutation.isPending,
      isMoGenerationActive,
      isCaseManagerActor,
      isCaseManagerProviderReviewLocked,
      isInlineSuggestionsView,
      useInlineSuggestionMode,
      getInlineSuggestionFieldValue,
      handleInlineSuggestionFieldChange,
      isInlineSuggestionVisible,
      isInlineSuggestionConditionallyRequired,
      isInlineSuggestionOptionConditionallyRequired,
      getInlineSuggestionStateForField,
      handleOpenInlineSuggestionDetail,
      handleOpenInlineSuggestionEvidence,
      handleOpenInlineGenerationFailure,
      handleOpenInlineRejectedSuggestions,
    ],
  );

  // Handle clicking the comments notification banner
  const handleCommentsNotificationClick = useCallback(() => {
    if (commentFieldIds.length > 0) {
      const firstFieldId = commentFieldIds[0];
      openReviewPanel(
        firstFieldId,
        getFieldLabelByKey(firstFieldId),
        'comments',
      );
    }
  }, [commentFieldIds, getFieldLabelByKey, openReviewPanel]);
  const handleSuggestionsNotificationClick = useCallback(() => {
    const nextStatusFilter = defaultSuggestionStatusFilter;
    const nextFieldIds = getSuggestionFieldIdsForFilter(nextStatusFilter);

    setActiveSuggestionStatusFilter(nextStatusFilter);

    if (nextFieldIds.length > 0) {
      const firstFieldId = nextFieldIds[0];
      openReviewPanel(
        firstFieldId,
        getFieldLabelByKey(firstFieldId),
        'suggestions',
        { scroll: false },
      );
    }
  }, [
    defaultSuggestionStatusFilter,
    getFieldLabelByKey,
    getSuggestionFieldIdsForFilter,
    openReviewPanel,
  ]);

  useEffect(() => {
    const pendingNavigation = pendingSuggestionReviewNavigationRef.current;
    if (!pendingNavigation) return;

    if (
      activeReviewTab !== 'suggestions' ||
      !activeReviewFieldId ||
      activeReviewFieldId !== pendingNavigation.fieldId
    ) {
      pendingSuggestionReviewNavigationRef.current = null;
      return;
    }

    const nextFieldId = findNearestRemainingReviewField({
      previousFieldIds: pendingNavigation.previousFieldIds,
      nextFieldIds: filteredSuggestionFieldIds,
      currentFieldId: pendingNavigation.fieldId,
    });

    pendingSuggestionReviewNavigationRef.current = null;

    if (!nextFieldId || nextFieldId === pendingNavigation.fieldId) return;

    openReviewPanel(
      nextFieldId,
      getFieldLabelByKey(nextFieldId),
      'suggestions',
      { scroll: false },
    );
  }, [
    activeReviewFieldId,
    activeReviewTab,
    filteredSuggestionFieldIds,
    getFieldLabelByKey,
    openReviewPanel,
  ]);

  useEffect(() => {
    const previousFieldIds = previousFilteredSuggestionFieldIdsRef.current;
    previousFilteredSuggestionFieldIdsRef.current = filteredSuggestionFieldIds;

    if (activeReviewTab !== 'suggestions') return;
    if (!activeReviewFieldId) return;
    if (pendingSuggestionReviewNavigationRef.current) return;
    if (pendingSuggestionComposerFieldId === activeReviewFieldId) return;
    // Move on only when THIS field just lost its last pending suggestion (an
    // accept, reject or dismiss), never away from a field that had none
    // (Nic 2026-11-04, Jennifer on Brooke McNicholas's TMD DBQ): switching
    // to the Comments tab and back cleared the composer guard, so the panel
    // jumped from Left Comments to the first field with a pending suggestion
    // (Right Comments, labeled the same) and her suggestions saved there.
    const nextFieldId = reviewFieldToMoveTo({
      previousFieldIds,
      nextFieldIds: filteredSuggestionFieldIds,
      currentFieldId: activeReviewFieldId,
    });
    if (!nextFieldId) return;

    openReviewPanel(
      nextFieldId,
      getFieldLabelByKey(nextFieldId),
      'suggestions',
      { scroll: false },
    );
  }, [
    activeReviewFieldId,
    activeReviewTab,
    filteredSuggestionFieldIds,
    getFieldLabelByKey,
    openReviewPanel,
    pendingSuggestionComposerFieldId,
  ]);

  useEffect(() => {
    if (activeReviewTab !== 'suggestions') return;

    if (
      !availableSuggestionStatusFilters.includes(activeSuggestionStatusFilter)
    ) {
      const fallbackStatusFilter =
        availableSuggestionStatusFilters[0] ||
        SUGGESTION_STATUS_FILTERS.pending;
      setActiveSuggestionStatusFilter(fallbackStatusFilter);
      return;
    }

    if (filteredSuggestionFieldIds.length === 0) {
      if (suggestionFieldIds.length === 0) {
        handleReviewClose();
      }
      return;
    }

    // Only auto-navigate if no field is currently active (e.g. initial open).
    // Don't force-navigate away from the user's current field.
    if (!activeReviewFieldId) {
      const firstFieldId = filteredSuggestionFieldIds[0];
      openReviewPanel(
        firstFieldId,
        getFieldLabelByKey(firstFieldId),
        'suggestions',
      );
    }
  }, [
    activeReviewFieldId,
    activeReviewTab,
    activeSuggestionStatusFilter,
    availableSuggestionStatusFilters,
    filteredSuggestionFieldIds,
    getFieldLabelByKey,
    handleReviewClose,
    openReviewPanel,
    suggestionFieldIds.length,
  ]);

  // Current user info for comments
  const currentUserInfo = useMemo(
    () => ({
      userId: user?.userId,
      name:
        user?.userFirstName && user?.userLastName
          ? `${user.userFirstName} ${user.userLastName}`
          : user?.userEmail || 'Unknown User',
    }),
    [user],
  );

  const canResolveComment = useCallback(
    (comment) => {
      if (canResolveAnyComment) return true;
      if (!isCaseManagerActor) return false;

      const actorUserId = currentUserInfo?.userId;
      const commentAuthorUserId = comment?.author?.userId;

      return Boolean(
        actorUserId &&
          commentAuthorUserId &&
          actorUserId === commentAuthorUserId,
      );
    },
    [canResolveAnyComment, currentUserInfo?.userId, isCaseManagerActor],
  );

  const handleAddSuggestion = useCallback(
    (fieldId, suggestedValue, note) => {
      createSuggestionMutation.mutate({
        userDbqId,
        fieldId,
        suggestedValue,
        note,
      });
    },
    [createSuggestionMutation, userDbqId],
  );

  const handleSaveSuggestionDraft = useCallback(
    async (suggestionId, options = {}) => {
      const normalizedSuggestionId = String(suggestionId || '').trim();
      const editedValue = String(options?.editedValue || '').trim();
      const suggestion = options?.suggestion || null;
      const fieldId = String(suggestion?.fieldId || '').trim();
      const isMedicalOpinionDraft =
        suggestion?.source === 'ai_generated' &&
        suggestion?.aiDraft?.draftType === 'medical_opinion';

      if (
        !normalizedSuggestionId ||
        normalizedSuggestionId !==
          String(suggestion?.suggestionId || '').trim() ||
        !editedValue ||
        !fieldId ||
        !isMedicalOpinionDraft
      ) {
        return;
      }

      if (canManageMoRemarks) {
        try {
          const saveResult = await saveMoRemarksFullText(
            userDbqId,
            editedValue,
          );
          syncDbqMutationResult(saveResult, 'Medical Opinion draft updated');
          return;
        } catch (error) {
          const errorMessage = String(error?.response?.data?.msg || '').trim();
          const canFallbackToSuggestionDraftUpdate =
            errorMessage ===
              'This DBQ does not have a generated Medical Opinion to edit' ||
            errorMessage ===
              'This DBQ does not have an editable Medical Opinion draft';

          if (!canFallbackToSuggestionDraftUpdate) {
            toast.error(
              errorMessage || 'Failed to update full Medical Opinion',
            );
            throw error;
          }
        }
      }

      const suggestionUpdatePayload = {
        userDbqId,
        fieldId,
        suggestedValue: editedValue,
      };
      if (Array.isArray(options?.sourceEvidenceRefs)) {
        suggestionUpdatePayload.sourceEvidenceRefs = options.sourceEvidenceRefs;
      }
      if (Array.isArray(options?.transcriptRefs)) {
        suggestionUpdatePayload.transcriptRefs = options.transcriptRefs;
      }

      const updateResult = await upsertDbqSuggestion(suggestionUpdatePayload);
      syncDbqMutationResult(updateResult, 'Medical Opinion draft updated');
    },
    [canManageMoRemarks, syncDbqMutationResult, userDbqId],
  );

  const handleAcceptSuggestion = useCallback(
    async (suggestionId, options = {}) => {
      queueSuggestionReviewNavigation();

      const editedValue = String(options?.editedValue || '').trim();
      const currentSuggestionValue = String(
        options?.suggestion?.payload?.value || '',
      ).trim();
      const isEditableMedicalOpinionDraft =
        options?.suggestion?.source === 'ai_generated' &&
        options?.suggestion?.aiDraft?.draftType === 'medical_opinion';

      if (
        isEditableMedicalOpinionDraft &&
        editedValue &&
        editedValue !== currentSuggestionValue
      ) {
        try {
          const saveResult = await saveMoRemarksFullText(
            userDbqId,
            editedValue,
          );
          syncDbqMutationResult(saveResult);
        } catch (error) {
          toast.error(
            error?.response?.data?.msg ||
              'Failed to update full Medical Opinion',
          );
          throw error;
        }
      }

      const mutationPayload = {
        userDbqId,
        suggestionId,
      };
      if (Array.isArray(options?.sourceEvidenceRefs)) {
        mutationPayload.sourceEvidenceRefs = options.sourceEvidenceRefs;
      }
      if (Array.isArray(options?.transcriptRefs)) {
        mutationPayload.transcriptRefs = options.transcriptRefs;
      }

      if (
        !isEditableMedicalOpinionDraft &&
        editedValue &&
        editedValue !== currentSuggestionValue
      ) {
        mutationPayload.editedValue = editedValue;
      }

      return acceptSuggestionMutation.mutateAsync(mutationPayload);
    },
    [
      acceptSuggestionMutation,
      queueSuggestionReviewNavigation,
      syncDbqMutationResult,
      userDbqId,
    ],
  );

  const handleRejectSuggestion = useCallback(
    (suggestionId) => {
      queueSuggestionReviewNavigation();
      rejectSuggestionMutation.mutate({
        userDbqId,
        suggestionId,
      });
    },
    [queueSuggestionReviewNavigation, rejectSuggestionMutation, userDbqId],
  );

  const handleDismissSuggestion = useCallback(
    (suggestionId) => {
      queueSuggestionReviewNavigation();
      return dismissSuggestionMutation.mutateAsync({
        userDbqId,
        suggestionId,
      });
    },
    [dismissSuggestionMutation, queueSuggestionReviewNavigation, userDbqId],
  );

  const handleSearchMissingSuggestionDetail = useCallback(
    async ({
      suggestionId,
      missingInput,
      missingInputIndex,
      currentDraftText,
    } = {}) => {
      const response = await axios.post(
        `/dbq/user-dbq/${userDbqId}/suggestions/${suggestionId}/missing-detail/search`,
        {
          missingInputKey: missingInput?.key || null,
          missingInputIndex,
          currentDraftText,
        },
      );
      return response.data?.missingDetailSearch || response.data;
    },
    [userDbqId],
  );

  const inlineMissingDetailInputs = useMemo(
    () => getInlineSuggestionMissingInputs(inlineMissingDetailSuggestion),
    [inlineMissingDetailSuggestion],
  );

  const closeInlineMissingDetailModal = useCallback(
    (force = false) => {
      if (isInlineMissingDetailSaving && !force) return;
      setInlineMissingDetailSuggestion(null);
      setInlineMissingDetailFieldId(null);
      setInlineMissingDetailDraftText('');
      setInlineMissingInputValues({});
      setInlineMissingInputSearches({});
      setInlineMissingDetailEvidencePatch({
        sourceEvidenceRefs: [],
        transcriptRefs: [],
      });
    },
    [isInlineMissingDetailSaving],
  );

  const handleInlineMissingInputValueChange = useCallback(
    (missingInput, index, nextValue) => {
      const stateKey = buildInlineMissingInputStateKey(missingInput, index);
      setInlineMissingInputValues((currentValues) => ({
        ...currentValues,
        [stateKey]: nextValue,
      }));
    },
    [],
  );

  const handleApplyInlineMissingInputValue = useCallback(
    (missingInput, index) => {
      const stateKey = buildInlineMissingInputStateKey(missingInput, index);
      const replacementValue = String(
        inlineMissingInputValues?.[stateKey] || '',
      ).trim();
      const searchState = inlineMissingInputSearches?.[stateKey] || null;
      const token = String(
        missingInput?.token || MISSING_USER_INPUT_TOKEN,
      ).trim();
      if (!replacementValue || !token) return;

      setInlineMissingDetailDraftText((currentValue) =>
        replaceInlineMissingInputOccurrence(
          currentValue,
          token,
          Number.isInteger(missingInput?.occurrenceIndex)
            ? missingInput.occurrenceIndex
            : null,
          replacementValue,
        ),
      );
      if (searchState?.status === 'found') {
        const evidencePatch = buildMissingDetailEvidencePatch(searchState);
        if (hasMissingDetailEvidencePatch(evidencePatch)) {
          setInlineMissingDetailEvidencePatch((currentPatch) =>
            mergeMissingDetailEvidencePatch(currentPatch, evidencePatch),
          );
        }
      }
    },
    [inlineMissingInputSearches, inlineMissingInputValues],
  );

  const handleSearchInlineMissingInputValue = useCallback(
    async (missingInput, index) => {
      if (!inlineMissingDetailSuggestion) return;

      const stateKey = buildInlineMissingInputStateKey(missingInput, index);
      setInlineMissingInputSearches((currentSearches) => ({
        ...currentSearches,
        [stateKey]: {
          status: 'searching',
          message: 'Searching selected evidence and transcripts...',
        },
      }));

      try {
        const result = await handleSearchMissingSuggestionDetail({
          suggestionId: inlineMissingDetailSuggestion.suggestionId,
          missingInput,
          missingInputIndex: index,
          currentDraftText: inlineMissingDetailDraftText,
        });
        const foundValue = String(result?.value || result?.answer || '').trim();

        if (result?.found && foundValue) {
          setInlineMissingInputValues((currentValues) => ({
            ...currentValues,
            [stateKey]: foundValue,
          }));
          setInlineMissingInputSearches((currentSearches) => ({
            ...currentSearches,
            [stateKey]: {
              status: 'found',
              message:
                result?.explanation ||
                'AI found a supported value. Review it, then insert it if correct.',
              ...buildMissingDetailEvidencePatch(result),
            },
          }));
          return;
        }

        setInlineMissingInputSearches((currentSearches) => ({
          ...currentSearches,
          [stateKey]: {
            status: 'not_found',
            message:
              result?.explanation ||
              'AI could not find this specific detail in the selected evidence or transcripts.',
          },
        }));
      } catch (error) {
        setInlineMissingInputSearches((currentSearches) => ({
          ...currentSearches,
          [stateKey]: {
            status: 'error',
            message:
              error?.response?.data?.msg ||
              error?.message ||
              'Failed to search selected evidence and transcripts.',
          },
        }));
      }
    },
    [
      handleSearchMissingSuggestionDetail,
      inlineMissingDetailDraftText,
      inlineMissingDetailSuggestion,
    ],
  );

  const handleSaveInlineMissingDetail = useCallback(async () => {
    if (!inlineMissingDetailFieldId || !inlineMissingDetailSuggestion) return;
    const nextDraftText = String(inlineMissingDetailDraftText || '').trim();
    if (!nextDraftText) return;

    setIsInlineMissingDetailSaving(true);
    try {
      const inlineSuggestionUpdate = {
        fieldId: inlineMissingDetailFieldId,
        suggestedValue: nextDraftText,
      };
      if (hasMissingDetailEvidencePatch(inlineMissingDetailEvidencePatch)) {
        inlineSuggestionUpdate.sourceEvidenceRefs =
          inlineMissingDetailEvidencePatch.sourceEvidenceRefs;
        inlineSuggestionUpdate.transcriptRefs =
          inlineMissingDetailEvidencePatch.transcriptRefs;
      }

      const inlineSuggestionResult = await upsertDbqSuggestion({
        userDbqId,
        ...inlineSuggestionUpdate,
      });

      if (inlineSuggestionResult?.userDbq?.formData) {
        skipNextAutoSaveRef.current = true;
        setFormData(inlineSuggestionResult.userDbq.formData);
        setInlineSuggestionDrafts(
          buildInlineSuggestionDraftsFromFormData(
            inlineSuggestionResult.userDbq.formData,
          ),
        );
        setInlineSuggestionDirtyFieldIds((currentDirtyFieldIds) => {
          const nextDirtyFieldIds = new Set(currentDirtyFieldIds);
          nextDirtyFieldIds.delete(inlineMissingDetailFieldId);
          return nextDirtyFieldIds;
        });
        queryClient.setQueryData(['userDbq', userDbqId], (current) => {
          if (!current?.userDbq) return current;
          return {
            ...current,
            userDbq: {
              ...current.userDbq,
              ...inlineSuggestionResult.userDbq,
            },
          };
        });
        queryClient.invalidateQueries(['user-dbq-audit', userDbqId]);
      }

      closeInlineMissingDetailModal(true);
    } catch (error) {
      toast.error(
        error?.response?.data?.msg || 'Failed to save missing detail',
      );
    } finally {
      setIsInlineMissingDetailSaving(false);
    }
  }, [
    closeInlineMissingDetailModal,
    inlineMissingDetailDraftText,
    inlineMissingDetailEvidencePatch,
    inlineMissingDetailFieldId,
    inlineMissingDetailSuggestion,
    queryClient,
    setFormData,
    userDbqId,
  ]);

  // Load initial form data
  useEffect(() => {
    if (userDbq?.formData) {
      skipNextAutoSaveRef.current = true;
      if (useInlineSuggestionMode) {
        setInlineSuggestionDrafts((currentDrafts) => {
          const nextDrafts = buildInlineSuggestionDraftsFromFormData(
            userDbq.formData,
          );
          inlineSuggestionDirtyFieldIdsRef.current.forEach((fieldId) => {
            if (hasOwnValue(currentDrafts, fieldId)) {
              nextDrafts[fieldId] = currentDrafts[fieldId];
            }
          });
          return nextDrafts;
        });
        setInlineSuggestionDirtyFieldIds((currentDirtyFieldIds) =>
          currentDirtyFieldIds.size > 0
            ? new Set(currentDirtyFieldIds)
            : currentDirtyFieldIds,
        );
      } else {
        setInlineSuggestionDrafts({});
        setInlineSuggestionDirtyFieldIds(new Set());
      }

      if (isMusculoskeletalRequestFlowDbq(userDbq)) {
        setFormData({
          ...userDbq.formData,
          _supplemental: {
            ...(userDbq.formData?._supplemental || {}),
            musculoskeletal: normalizeMusculoskeletalSupplementalData(
              userDbq.formData?._supplemental?.musculoskeletal,
            ),
          },
        });
        return;
      }

      setFormData(userDbq.formData);
    }
  }, [userDbq, setFormData, useInlineSuggestionMode]);

  useEffect(() => {
    if (!isMusculoskeletal) {
      if (supplementalValidationErrors.length > 0) {
        setSupplementalValidationErrors([]);
      }
      return;
    }

    if (supplementalValidationErrors.length > 0) {
      setSupplementalValidationErrors(
        validateMusculoskeletalSupplementalData(
          musculoskeletalSupplementalData,
        ),
      );
    }
  }, [
    isMusculoskeletal,
    musculoskeletalSupplementalData,
    supplementalValidationErrors.length,
  ]);

  // Load required fields from template and user DBQ
  useEffect(() => {
    if (userDbq) {
      // Combined required fields (for form validation)
      setRequiredFields(userDbq.requiredFields || []);
      // Template-level required fields (affects all DBQs of this type)
      setTemplateRequiredFields(userDbq.templateRequiredFields || []);
      // User DBQ-level required fields (affects only this DBQ)
      setUserRequiredFields(userDbq.userRequiredFields || []);
    }
  }, [userDbq]);

  // Handle toggling required status for a field
  // Admins (admin:dbq_forms) see a modal to choose scope; others toggle "this DBQ only" directly
  const handleToggleRequired = useCallback(
    (fieldId) => {
      if (!canToggleRequiredPerDbq || !userDbq?.userDbqId) return;

      if (canManageFormTemplates) {
        // Admin: show scope chooser modal
        setPendingRequiredFieldId(fieldId);
        setShowRequiredFieldModal(true);
      } else {
        // Non-admin (claims:edit): apply directly to this DBQ only
        if (
          templateRequiredFields.includes(fieldId) &&
          !userRequiredFields.includes(fieldId)
        ) {
          // Field is required at template level — non-admins can't remove it
          toast.info(
            'This field is required for all DBQs of this type. Only a Claims Admin can change that.',
          );
          return;
        }

        const isCurrentlyRequired = userRequiredFields.includes(fieldId);
        const newUserRequiredFields = isCurrentlyRequired
          ? userRequiredFields.filter((id) => id !== fieldId)
          : [...userRequiredFields, fieldId];

        setUserRequiredFields(newUserRequiredFields);
        const newCombined = [
          ...new Set([...templateRequiredFields, ...newUserRequiredFields]),
        ];
        setRequiredFields(newCombined);

        updateUserDbqRequiredFieldsMutation.mutate({
          userDbqId: userDbq.userDbqId,
          requiredFields: newUserRequiredFields,
        });
      }
    },
    [
      canToggleRequiredPerDbq,
      canManageFormTemplates,
      userDbq?.userDbqId,
      userRequiredFields,
      templateRequiredFields,
      updateUserDbqRequiredFieldsMutation,
    ],
  );

  const scrollToSupplementalSection = useCallback(() => {
    const element =
      supplementalSectionRef.current ||
      document.getElementById('dbq-musculoskeletal-supplemental-section');

    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const firstInput = element.querySelector('input, textarea');
    if (firstInput && typeof firstInput.focus === 'function') {
      firstInput.focus();
    }
  }, []);

  const handleSupplementalChange = useCallback(
    (nextMusculoskeletalData) => {
      const normalizedData = normalizeMusculoskeletalSupplementalData(
        nextMusculoskeletalData,
      );

      if (isInlineSuggestionsView) {
        const currentDisplayedData = buildInlineMusculoskeletalSupplementalData(
          musculoskeletalSupplementalData,
          getInlineSuggestionFieldValue,
        );
        const changedFieldIds = getChangedMusculoskeletalSupplementalFieldIds(
          currentDisplayedData,
          normalizedData,
        );

        changedFieldIds.forEach((fieldId) => {
          handleInlineSuggestionFieldChange(
            fieldId,
            getMusculoskeletalSupplementalFieldValue(normalizedData, fieldId),
            [],
          );
        });

        if (supplementalValidationErrors.length > 0) {
          setSupplementalValidationErrors(
            validateMusculoskeletalSupplementalData(normalizedData),
          );
        }
        return;
      }

      setFormData((current) => ({
        ...(current || {}),
        _supplemental: {
          ...(current?._supplemental || {}),
          musculoskeletal: normalizedData,
        },
      }));

      if (supplementalValidationErrors.length > 0) {
        setSupplementalValidationErrors(
          validateMusculoskeletalSupplementalData(normalizedData),
        );
      }
    },
    [
      getInlineSuggestionFieldValue,
      handleInlineSuggestionFieldChange,
      isInlineSuggestionsView,
      musculoskeletalSupplementalData,
      setFormData,
      supplementalValidationErrors.length,
    ],
  );

  // Determine if the pending field is currently required, and at what level
  const pendingFieldStatus = useMemo(() => {
    if (!pendingRequiredFieldId) return { isRequired: false, level: null };

    const isInTemplate = templateRequiredFields.includes(
      pendingRequiredFieldId,
    );
    const isInUser = userRequiredFields.includes(pendingRequiredFieldId);

    return {
      isRequired: isInTemplate || isInUser,
      level: isInTemplate ? 'template' : isInUser ? 'user' : null,
      isInTemplate,
      isInUser,
    };
  }, [pendingRequiredFieldId, templateRequiredFields, userRequiredFields]);

  // Handle applying required field change at user DBQ level
  const handleApplyRequiredToUserDbq = useCallback(() => {
    if (!pendingRequiredFieldId || !userDbq?.userDbqId) return;

    const isCurrentlyRequired = userRequiredFields.includes(
      pendingRequiredFieldId,
    );
    const newUserRequiredFields = isCurrentlyRequired
      ? userRequiredFields.filter((id) => id !== pendingRequiredFieldId)
      : [...userRequiredFields, pendingRequiredFieldId];

    setUserRequiredFields(newUserRequiredFields);
    // Also update combined required fields
    const newCombined = [
      ...new Set([...templateRequiredFields, ...newUserRequiredFields]),
    ];
    setRequiredFields(newCombined);

    updateUserDbqRequiredFieldsMutation.mutate({
      userDbqId: userDbq.userDbqId,
      requiredFields: newUserRequiredFields,
    });

    setShowRequiredFieldModal(false);
    setPendingRequiredFieldId(null);
  }, [
    pendingRequiredFieldId,
    userDbq?.userDbqId,
    userRequiredFields,
    templateRequiredFields,
    updateUserDbqRequiredFieldsMutation,
  ]);

  // Handle applying required field change at template level (all DBQs of this type)
  const handleApplyRequiredToTemplate = useCallback(() => {
    if (!pendingRequiredFieldId || !userDbq?.dbqFormId) return;

    const isCurrentlyRequired = templateRequiredFields.includes(
      pendingRequiredFieldId,
    );
    const newTemplateRequiredFields = isCurrentlyRequired
      ? templateRequiredFields.filter((id) => id !== pendingRequiredFieldId)
      : [...templateRequiredFields, pendingRequiredFieldId];

    setTemplateRequiredFields(newTemplateRequiredFields);
    // Also update combined required fields
    const newCombined = [
      ...new Set([...newTemplateRequiredFields, ...userRequiredFields]),
    ];
    setRequiredFields(newCombined);

    updateRequiredFieldsMutation.mutate({
      dbqFormId: userDbq.dbqFormId,
      requiredFields: newTemplateRequiredFields,
    });

    setShowRequiredFieldModal(false);
    setPendingRequiredFieldId(null);
  }, [
    pendingRequiredFieldId,
    userDbq?.dbqFormId,
    templateRequiredFields,
    userRequiredFields,
    updateRequiredFieldsMutation,
  ]);

  // Handle canceling the required field modal
  const handleCancelRequiredModal = useCallback(() => {
    setShowRequiredFieldModal(false);
    setPendingRequiredFieldId(null);
  }, []);

  const applyDbqFieldChange = useCallback(
    (fieldId, value, instancePath = []) => {
      if (isInlineSuggestionsView) {
        return handleInlineSuggestionFieldChange(fieldId, value, instancePath);
      }

      return formContext.handleFieldChange(fieldId, value, instancePath);
    },
    [formContext, handleInlineSuggestionFieldChange, isInlineSuggestionsView],
  );

  // Handle field change with optional confirmation dialog
  // optionId: the option being selected (used for confirmation check)
  // valueToSet: optional, the actual value to set (defaults to optionId)
  const handleFieldChangeWithConfirmation = useCallback(
    (field, optionId, instancePath, valueToSet) => {
      const actualValue = valueToSet !== undefined ? valueToSet : optionId;
      // Check if this option requires confirmation
      if (field.confirmation?.options?.includes(optionId)) {
        setPendingFieldChange({
          fieldId: field.id,
          value: actualValue,
          instancePath,
          message: field.confirmation.message,
        });
        setShowFieldConfirmation(true);
      } else {
        applyDbqFieldChange(field.id, actualValue, instancePath);
      }
    },
    [applyDbqFieldChange],
  );

  // Confirm the pending field change
  const confirmFieldChange = useCallback(() => {
    if (pendingFieldChange) {
      applyDbqFieldChange(
        pendingFieldChange.fieldId,
        pendingFieldChange.value,
        pendingFieldChange.instancePath,
      );
    }
    setShowFieldConfirmation(false);
    setPendingFieldChange(null);
  }, [applyDbqFieldChange, pendingFieldChange]);

  // Cancel the pending field change (keep previous value)
  const cancelFieldChange = useCallback(() => {
    setShowFieldConfirmation(false);
    setPendingFieldChange(null);
  }, []);

  // Save handler - defined before functions that depend on it
  const handleSave = useCallback(
    async (isAutoSave = false) => {
      try {
        // Stale tab: someone else saved since this tab loaded. Saving now
        // would overwrite their work with stale state - refuse until refresh.
        if (isStaleRef.current) {
          if (!isAutoSave) {
            toast.error(
              'This DBQ was updated by someone else. Refresh to see the latest before saving.',
            );
          }
          return false;
        }

        saveInFlightRef.current = true;
        if (!isAutoSave) setIsSaving(true);

        if (isInlineSuggestionsView) {
          if (
            !canCreateSuggestions ||
            inlineSuggestionDirtyFieldIds.size === 0
          ) {
            return true;
          }

          const savedInlineSuggestionFieldIds = Array.from(
            inlineSuggestionDirtyFieldIds,
          );
          const inlineSuggestionPayload = savedInlineSuggestionFieldIds.map(
            (fieldId) => ({
              fieldId,
              suggestedValue: inlineSuggestionDrafts[fieldId],
            }),
          );
          const savedInlineSuggestionValues = inlineSuggestionPayload.reduce(
            (valuesByFieldId, inlineSuggestion) => ({
              ...valuesByFieldId,
              [inlineSuggestion.fieldId]: inlineSuggestion.suggestedValue,
            }),
            {},
          );

          let latestInlineSuggestionResult = null;
          for (const inlineSuggestion of inlineSuggestionPayload) {
            latestInlineSuggestionResult = await upsertDbqSuggestion({
              userDbqId,
              ...inlineSuggestion,
            });
          }

          if (latestInlineSuggestionResult?.userDbq?.formData) {
            skipNextAutoSaveRef.current = true;
            setFormData(latestInlineSuggestionResult.userDbq.formData);
            setInlineSuggestionDrafts((currentDrafts) => {
              const nextDrafts = buildInlineSuggestionDraftsFromFormData(
                latestInlineSuggestionResult.userDbq.formData,
              );

              inlineSuggestionDirtyFieldIdsRef.current.forEach((fieldId) => {
                const valueChangedAfterSaveStarted =
                  !areInlineSuggestionValuesEqual(
                    currentDrafts[fieldId],
                    savedInlineSuggestionValues[fieldId],
                  );
                if (
                  !savedInlineSuggestionFieldIds.includes(fieldId) ||
                  valueChangedAfterSaveStarted
                ) {
                  nextDrafts[fieldId] = currentDrafts[fieldId];
                }
              });

              return nextDrafts;
            });
            setInlineSuggestionDirtyFieldIds((currentDirtyFieldIds) => {
              const nextDirtyFieldIds = new Set(currentDirtyFieldIds);
              savedInlineSuggestionFieldIds.forEach((fieldId) => {
                const currentDraftValue =
                  inlineSuggestionDraftsRef.current[fieldId];
                if (
                  areInlineSuggestionValuesEqual(
                    currentDraftValue,
                    savedInlineSuggestionValues[fieldId],
                  )
                ) {
                  nextDirtyFieldIds.delete(fieldId);
                }
              });
              return nextDirtyFieldIds;
            });
            queryClient.setQueryData(['userDbq', userDbqId], (current) => {
              if (!current?.userDbq) return current;
              return {
                ...current,
                userDbq: {
                  ...current.userDbq,
                  ...latestInlineSuggestionResult.userDbq,
                },
              };
            });
          }

          queryClient.invalidateQueries(['user-dbq-audit', userDbqId]);

          if (!isAutoSave) {
            setShowSaveSuccess(true);
            setTimeout(() => setShowSaveSuccess(false), 3000);
          }
          return true;
        }

        // 1. Filter stale data from inactive repeating instances
        let cleanedFormData = filterFormDataByActiveInstances(
          formData,
          repeatingInstances,
        );

        // 2. Filter hidden field values (child_elements of unselected options, etc.)
        cleanedFormData = filterHiddenFieldValues(cleanedFormData, formJson);

        const payloadFormData = buildRoleScopedSavePayload({
          cleanedFormData,
        });

        console.log('[handleSave] Saving cleaned formData:', payloadFormData);

        const saveResponse = await axios.put(`/dbq/user-dbq/${userDbqId}`, {
          formData: payloadFormData,
        });

        // Post-sign long-text edits are stamped SERVER-side; sync just that
        // marker back into local state so the "Edited after signing" badges
        // stay live - including DISAPPEARING when a revert cleared the last
        // stamp (the server deletes the key entirely then). Metadata-only, and
        // only when the stamps actually changed: an unconditional setFormData
        // would run the autosave effect's cleanup on every save and cancel a
        // debounce scheduled by keystrokes typed while the request was in
        // flight, silently dropping their autosave.
        if (saveResponse?.data?.userDbq?.updatedAt) {
          advanceKnownUpdatedAt(saveResponse.data.userDbq.updatedAt);
        }

        const serverSavedFormData = saveResponse?.data?.userDbq?.formData;
        if (serverSavedFormData) {
          const serverPostSignEdits =
            serverSavedFormData._postSignEdits || null;
          const sentPostSignEdits = formData?._postSignEdits || null;
          const stampsChanged =
            JSON.stringify(serverPostSignEdits) !==
            JSON.stringify(sentPostSignEdits);
          if (stampsChanged) {
            skipNextAutoSaveRef.current = true;
            setFormData((current) => {
              const next = { ...current };
              if (serverPostSignEdits)
                next._postSignEdits = serverPostSignEdits;
              else delete next._postSignEdits;
              return next;
            });
          }
        }

        queryClient.invalidateQueries(['user-dbq-audit', userDbqId]);

        if (!isAutoSave) {
          setShowSaveSuccess(true);
          setTimeout(() => setShowSaveSuccess(false), 3000);
        }
        return true;
      } catch (err) {
        console.error('Error saving:', err);
        if (!isAutoSave) {
          alert('Failed to save form data');
        }
        return false;
      } finally {
        saveInFlightRef.current = false;
        if (!isAutoSave) setIsSaving(false);
      }
    },
    [
      userDbqId,
      formData,
      formJson,
      repeatingInstances,
      filterFormDataByActiveInstances,
      filterHiddenFieldValues,
      queryClient,
      isInlineSuggestionsView,
      canCreateSuggestions,
      inlineSuggestionDirtyFieldIds,
      inlineSuggestionDrafts,
      setFormData,
      advanceKnownUpdatedAt,
    ],
  );

  // Navigate to a missing required field
  const handleGoToMissingField = useCallback(
    (field) => {
      setTimeout(() => {
        const element = findNavigableElementForField({
          fieldId: field.id,
          optionId: field.optionId,
        });
        focusNavigableElement(element);
      }, 100);
    },
    [findNavigableElementForField, focusNavigableElement],
  );

  // QA Changes panel: clicking a row scrolls to the field (same resolver the
  // missing-required panel uses - fields carry data-field-id anchors) and
  // flashes it so the eye lands on the right control.
  const handleJumpToChangedField = useCallback(
    (fieldKey) => {
      const element = findNavigableElementForField({ fieldId: fieldKey });
      focusNavigableElement(element);
      if (element && typeof element.animate === 'function') {
        element.animate(
          [
            { boxShadow: '0 0 0 4px rgba(245, 158, 11, 0.55)' },
            { boxShadow: '0 0 0 6px rgba(245, 158, 11, 0)' },
          ],
          { duration: 1400, easing: 'ease-out' },
        );
      }
    },
    [findNavigableElementForField, focusNavigableElement],
  );

  // Visibility test for the Changes panel: a row for a currently-HIDDEN
  // field is held back (restore-order guarantee - its controller must come
  // back first). Reuses the exact visible-set computation the save pipeline
  // uses (filterHiddenFieldValues), so the panel and the saves always agree
  // on what "hidden" means. Underscore keys (MSK supplemental) are gated by
  // their own question flow, not template visibility - always shown.
  const changesPanelFieldVisible = useMemo(() => {
    if (!formJson || !formData) return null;
    const visibleKeys = new Set(
      Object.keys(filterHiddenFieldValues(formData, formJson) || {}),
    );
    return (fieldKey) => fieldKey.startsWith('_') || visibleKeys.has(fieldKey);
  }, [formData, formJson, filterHiddenFieldValues]);

  // Swap a changed field back to the value the provider signed. Writes the
  // signed value through normal form state - the autosave persists it and the
  // server's stamp logic clears the entry (value matches signed again).
  // Dotted keys (_supplemental.musculoskeletal.*) set their nested path.
  // The managed MO remarks field is server-PROTECTED against direct field
  // writes (updateUserDbq skips it), so its swap-back must go through the MO
  // save endpoint - same path the old badge revert used.
  const handleSwapBackToSigned = useCallback(
    (fieldKey, signedValue) => {
      if (
        isManagedMedicalOpinionRemarks &&
        remarksFieldId &&
        fieldKey === remarksFieldId
      ) {
        if (!signedValue) {
          toast.error(
            'The signed Medical Opinion text is empty; edit it through the full Medical Opinion editor instead.',
          );
          return;
        }
        saveRemarksFullTextMutation.mutate({
          fullMedicalOpinionText: signedValue,
        });
        return;
      }
      setFormData((current) => {
        if (!fieldKey.includes('.')) {
          return { ...current, [fieldKey]: signedValue };
        }
        const segments = fieldKey.split('.');
        const next = { ...current };
        let cursor = next;
        for (let i = 0; i < segments.length - 1; i += 1) {
          const segment = segments[i];
          cursor[segment] =
            cursor[segment] && typeof cursor[segment] === 'object'
              ? { ...cursor[segment] }
              : {};
          cursor = cursor[segment];
        }
        cursor[segments[segments.length - 1]] = signedValue;
        return next;
      });
    },
    [
      setFormData,
      isManagedMedicalOpinionRemarks,
      remarksFieldId,
      saveRemarksFullTextMutation,
    ],
  );

  // Handle "Sign and Submit" for providers
  const handleSignAndSubmit = useCallback(() => {
    const missingFields = getMissingRequiredFields(formJson, requiredFields);

    if (missingFields.length > 0) {
      setMissingRequiredFieldsList(missingFields);

      // Scroll to the validation panel at the top
      setTimeout(() => {
        const panel = document.querySelector('.dbq-validation-panel');
        if (panel) {
          panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 50);
    } else {
      if (pendingSuggestionCount > 0) {
        const firstPendingSuggestionFieldId = getSuggestionFieldIdsForFilter(
          SUGGESTION_STATUS_FILTERS.pending,
        )[0];

        setActiveSuggestionStatusFilter(SUGGESTION_STATUS_FILTERS.pending);
        if (firstPendingSuggestionFieldId) {
          openReviewPanel(
            firstPendingSuggestionFieldId,
            getFieldLabelByKey(firstPendingSuggestionFieldId),
            'suggestions',
          );
        }

        toast.error(
          `Resolve all suggested edits before ${isProviderConfirmingChanges ? 'confirming changes' : 'signing'}. ${pendingSuggestionCount} pending suggestion${pendingSuggestionCount === 1 ? '' : 's'} remain.`,
        );
        return;
      }

      if (isMusculoskeletal) {
        const nextSupplementalErrors = validateMusculoskeletalSupplementalData(
          musculoskeletalSupplementalData,
        );

        if (nextSupplementalErrors.length > 0) {
          setSupplementalValidationErrors(nextSupplementalErrors);
          toast.error(nextSupplementalErrors[0]);
          setTimeout(() => {
            scrollToSupplementalSection();
          }, 50);
          return;
        }

        setSupplementalValidationErrors([]);
      }

      setMissingRequiredFieldsList([]);
      // Reset to default profile when opening the modal
      if (providerProfiles.length > 0) {
        const defaultProfile =
          providerProfiles.find((p) => p.isDefault) || providerProfiles[0];
        if (defaultProfile.profileId !== selectedProfileId) {
          // Use the profile select handler to reset certification data
          const fullName = providerProfile
            ? `${providerProfile.userFirstName || ''} ${providerProfile.userLastName || ''}`.trim()
            : '';
          const nameWithTitle = defaultProfile.title
            ? `${fullName}, ${defaultProfile.title}`
            : fullName;
          const licenseInfo =
            defaultProfile.licenseNumber && defaultProfile.licenseState
              ? `${defaultProfile.licenseNumber} — ${defaultProfile.licenseState}`
              : defaultProfile.licenseNumber || '';

          setSelectedProfileId(defaultProfile.profileId);
          setCertificationData((prev) => ({
            ...prev,
            signature: fullName,
            nameAndTitle: nameWithTitle,
            specialty: defaultProfile.specialty || '',
            phoneOrFax:
              defaultProfile.businessPhone || providerProfile?.userPhone || '',
            npi: defaultProfile.npi || '',
            licenseNumberAndState: licenseInfo,
            address: defaultProfile.address || '',
          }));
        }
      }
      // All required fields are filled - show signing modal
      setShowSigningModal(true);
    }
  }, [
    formJson,
    requiredFields,
    getMissingRequiredFields,
    isMusculoskeletal,
    pendingSuggestionCount,
    musculoskeletalSupplementalData,
    getFieldLabelByKey,
    getSuggestionFieldIdsForFilter,
    isProviderConfirmingChanges,
    openReviewPanel,
    providerProfiles,
    providerProfile,
    scrollToSupplementalSection,
    selectedProfileId,
  ]);

  // Handle certification field changes
  const handleCertificationChange = useCallback((e) => {
    const { name, value } = e.target;
    setCertificationData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Handle profile tab selection
  const handleProfileSelect = useCallback(
    (profileId) => {
      const profile = providerProfiles.find((p) => p.profileId === profileId);
      if (!profile) return;

      setSelectedProfileId(profileId);

      const fullName = providerProfile
        ? `${providerProfile.userFirstName || ''} ${providerProfile.userLastName || ''}`.trim()
        : '';
      const nameWithTitle = profile.title
        ? `${fullName}, ${profile.title}`
        : fullName;

      const licenseInfo =
        profile.licenseNumber && profile.licenseState
          ? `${profile.licenseNumber} — ${profile.licenseState}`
          : profile.licenseNumber || '';

      setCertificationData((prev) => ({
        ...prev,
        signature: fullName,
        nameAndTitle: nameWithTitle,
        specialty: profile.specialty || '',
        phoneOrFax: profile.businessPhone || providerProfile?.userPhone || '',
        npi: profile.npi || '',
        licenseNumberAndState: licenseInfo,
        address: profile.address || '',
      }));
    },
    [providerProfile, providerProfiles],
  );

  // Handle signing submission
  const handleSignSubmit = useCallback(() => {
    // Save form data first, then sign
    handleSave(false).then((success) => {
      if (success) {
        signDbqMutation.mutate({
          userDbqId,
          certificationData,
        });
      }
    });
  }, [userDbqId, certificationData, signDbqMutation, handleSave]);

  const handleMarkReadyForReview = useCallback(() => {
    handleSave(false).then((success) => {
      if (success) {
        markReadyForReviewMutation.mutate(userDbqId);
      }
    });
  }, [handleSave, markReadyForReviewMutation, userDbqId]);

  // Initialize repeating instances on form load
  // Pass saved formData so we can restore instance IDs instead of generating new ones
  useEffect(() => {
    if (formJson) {
      initializeRepeatingInstances(formJson, userDbq?.formData || {});
    }
  }, [formJson, userDbq?.formData, initializeRepeatingInstances]);

  // Auto-save with debounce
  useEffect(() => {
    if (!userDbqId || !userDbq) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }

    // Set new timeout for auto-save
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      handleSave(true); // auto-save
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, [formData, inlineSuggestionRevision, userDbqId, userDbq, handleSave]);

  if (isLoading) {
    return (
      <div className="dbq-renderer">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '100vh' }}
        >
          <div className="text-center">
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              size="3x"
              className="text-primary mb-3"
            />
            <p className="text-muted">Loading form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dbq-renderer">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '100vh' }}
        >
          <Alert variant="danger">
            <strong>Error:</strong> Failed to load form.{' '}
            {error.response?.data?.msg || error.message}
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <>
      <UserDbqEditorRequestFlowChrome
        dbqStyles={dbqStyles}
        dbqStandaloneStyles={dbqStandaloneStyles}
        fixedHeaderStyles={fixedHeaderStyles}
        title={formJson?.title}
        showSaveSuccess={showSaveSuccess}
        useInlineSuggestionMode={useInlineSuggestionMode}
        inlineSuggestionViewMode={inlineSuggestionViewMode}
        onChangeInlineSuggestionViewMode={setInlineSuggestionViewMode}
        dismissedSuggestionCount={totalSuggestionStats.dismissed || 0}
        onOpenDismissedSuggestions={handleOpenDismissedSuggestions}
        isSaving={isSaving}
        onSave={handleSave}
        canProviderSubmit={canProviderSubmit}
        onSignAndSubmit={handleSignAndSubmit}
        providerSignActionLabel={providerSignActionLabel}
        canManageRequestWorkspace={canManageRequestWorkspace}
        canMarkReadyForReview={canMarkReadyForReview}
        isMarkingReadyForReview={markReadyForReviewMutation.isPending}
        onMarkReadyForReview={handleMarkReadyForReview}
        reviewActionLabel={reviewActionLabel}
        isInlineSuggestionsView={isInlineSuggestionsView}
        inlineSuggestionNavigatorCount={inlineSuggestionNavigatorCount}
        isSuggestionCountLoading={isSuggestionCountLoading}
        activeInlineSuggestionNavigatorIndex={
          activeInlineSuggestionNavigatorIndex
        }
        activeInlineSuggestionNavigatorLabel={
          activeInlineSuggestionNavigatorLabel
        }
        onInlineSuggestionNavigatorCurrent={
          handleInlineSuggestionNavigatorCurrent
        }
        onInlineSuggestionNavigatorMove={handleInlineSuggestionNavigatorMove}
      />

      {(providerSigned || finalizedLocked || isStale) && (
        // One sticky stack (Nic, 2026-08-04): the signed/finalized card with
        // the QA Changes button, and the stale-tab alert right below it, stay
        // visible however deep the form scrolls. Painted the page's grey so
        // the cards float on it instead of punching a white band into the
        // background.
        <div
          // Sticky position/top/z live in the stylesheet
          // (userDbqEditorRequestFlowStyles) so the top offset tracks the
          // fixed header's responsive height.
          className="dbq-post-sign-banner"
          style={{
            background: 'rgb(237, 242, 247)',
            padding: '16px 16px 12px',
          }}
        >
          {(providerSigned || finalizedLocked) && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '12px 16px',
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              border: 'none',
              borderRadius: 12,
              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.3)',
            }}
          >
            <div
              style={{
                flex: '0 0 auto',
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255, 255, 255, 0.18)',
                color: '#ffffff',
              }}
            >
              <Lock size={17} strokeWidth={2.2} aria-hidden="true" />
            </div>
            <div style={{ minWidth: 0, flex: '1 1 auto' }}>
              <div
                style={{
                  fontWeight: 600,
                  color: '#ffffff',
                  fontSize: '0.92rem',
                }}
              >
                {finalizedLocked
                  ? 'Finalized.'
                  : `Signed by ${formData?._certification?.providerName || 'the provider'}${
                      formData?._certification?.signedAt
                        ? ` on ${new Date(formData._certification.signedAt).toLocaleDateString()}`
                        : ''
                    }.`}
              </div>
              <div
                style={{
                  color: 'rgba(255, 255, 255, 0.85)',
                  fontSize: '0.85rem',
                }}
              >
                {finalizedLocked
                  ? 'This DBQ is locked. Reopen it from the review workflow to make changes.'
                  : 'Everything stays editable until Finalize, and every change is recorded for QA review.'}
              </div>
            </div>
            {canFinalizeDbqReview && postSignEditCount > 0 && (
              <Button
                variant="light"
                size="sm"
                className="text-nowrap fw-semibold"
                onClick={() => setShowChangesPanel((open) => !open)}
              >
                Changes ({postSignEditCount})
              </Button>
            )}
          </div>
          )}
          {isStale && (
            <Alert
              variant="danger"
              className={`dbq-stale-banner mb-0 py-2 d-flex align-items-center justify-content-between gap-3 ${
                providerSigned || finalizedLocked ? 'mt-2' : ''
              }`}
            >
              <span>
                <strong>
                  This DBQ was updated by {staleActorName || 'someone else'}.
                </strong>{' '}
                Your changes are no longer saving. Refresh to see the latest
                version before continuing.
              </span>
              <Button
                variant="outline-danger"
                size="sm"
                className="text-nowrap"
                onClick={() => window.location.reload()}
              >
                Refresh now
              </Button>
            </Alert>
          )}
        </div>
      )}

      {canFinalizeDbqReview && showChangesPanel && (
        <DbqChangesPanel
          postSignEdits={postSignEdits}
          formData={formData}
          formJson={formJson}
          onJumpToField={handleJumpToChangedField}
          // A stale tab's saves are PAUSED - a Swap back would vanish locally
          // and never persist (pure confusion). View-only until refresh.
          onSwapBack={
            finalizedLocked || isStale ? null : handleSwapBackToSigned
          }
          staleNotice={isStale}
          isFieldVisible={changesPanelFieldVisible}
          onClose={() => setShowChangesPanel(false)}
        />
      )}

      <UserDbqEditorRequestFlowLayout
        formJson={formJson}
        showReviewPanel={showReviewPanel}
        validationPanelProps={{
          missingRequiredFieldsList,
          onDismissMissingRequiredFields: setMissingRequiredFieldsList,
          onGoToMissingField: handleGoToMissingField,
        }}
        notificationProps={{
          totalCommentCount,
          shouldShowSuggestionNotificationCount,
          onCommentsNotificationClick: handleCommentsNotificationClick,
          onSuggestionsNotificationClick: handleSuggestionsNotificationClick,
          isSuggestionCountLoading,
          displayedSuggestionCount,
        }}
        isCaseManagerProviderReviewLocked={isCaseManagerProviderReviewLocked}
        formViewProps={{
          formJson,
          formContext: enhancedFormContext,
          renderWithGroupMap,
          isAdmin: canToggleRequiredPerDbq,
          canComment: canCreateComments,
          canSuggest: useInlineSuggestionMode ? false : canCreateSuggestions,
          readOnly: isCaseManagerProviderReviewLocked || finalizedLocked,
          onCommentClick: openReviewPanel,
          onSuggestionClick:
            !useInlineSuggestionMode && canCreateSuggestions
              ? openSuggestionComposerPanel
              : undefined,
          activeCommentFieldId: activeReviewFieldId,
          activeSuggestionFieldId: activeReviewFieldId,
          requiredFields,
          onToggleRequired: canToggleRequiredPerDbq
            ? handleToggleRequired
            : undefined,
          onFieldChangeWithConfirmation: handleFieldChangeWithConfirmation,
        }}
        supplementalSectionRef={supplementalSectionRef}
        supplementalProps={{
          isMusculoskeletal,
          supplementalInsertAfterSectionId,
          displayedMusculoskeletalSupplementalData,
          onSupplementalChange: handleSupplementalChange,
          canEditMusculoskeletalSupplemental,
          editLocked: finalizedLocked,
          supplementalValidationErrors,
          activeReviewFieldId,
          canOpenReview: canComment || canAccessSuggestions,
          getReviewItemCount,
          onReviewClick: openReviewPanel,
          getInlineSuggestionStateForField,
          onOpenInlineSuggestionDetail: handleOpenInlineSuggestionDetail,
          onOpenInlineSuggestionEvidence: handleOpenInlineSuggestionEvidence,
          onOpenInlineGenerationFailure: handleOpenInlineGenerationFailure,
          onOpenInlineRejectedSuggestions: handleOpenInlineRejectedSuggestions,
        }}
        reviewPanelProps={
          showReviewPanel
            ? {
                fieldId: activeReviewFieldId,
                fieldLabel: activeReviewHeaderLabel,
                activeTab: activeReviewTab,
                onTabChange: setActiveReviewTab,
                onNavigate: handleReviewNavigate,
                onScrollToField: scrollToField,
                reviewFieldIds:
                  activeReviewTab === 'suggestions' && !isInlineSuggestionsView
                    ? filteredSuggestionFieldIds
                    : reviewFieldIds,
                onClose: handleReviewClose,
                commentCount: activeCommentCount,
                suggestionCount: isInlineSuggestionsView
                  ? 0
                  : activeSuggestionCount,
                commentsPanelProps: {
                  comments: activeComments,
                  canComment: canCreateComments,
                  canResolve: canManageFormTemplates,
                  canResolveComment: isCaseManagerProviderReviewLocked
                    ? () => false
                    : canResolveComment,
                  currentUser: currentUserInfo,
                  onResolve: isCaseManagerProviderReviewLocked
                    ? undefined
                    : resolveComment,
                  onReply: canCreateComments ? addReply : undefined,
                  onAddComment: canCreateComments ? addComment : undefined,
                  commentFieldIds,
                },
                suggestionsPanelProps: {
                  suggestions: activeSuggestions,
                  claimId: userDbq?.claimId,
                  suggestionStats: totalSuggestionStats,
                  activeStatusFilter: effectiveSuggestionStatusFilter,
                  onStatusFilterChange: handleSuggestionStatusFilterChange,
                  field: activeReviewField,
                  currentValue: activeReviewCurrentValue,
                  isProviderView: isProvider,
                  canSuggest:
                    !isInlineSuggestionsView && !useInlineSuggestionMode
                      ? canCreateSuggestions
                      : false,
                  canAcceptSuggestions: isInlineSuggestionsView
                    ? false
                    : canAcceptSuggestions,
                  canDismissSuggestions: isInlineSuggestionsView
                    ? false
                    : canDismissSuggestionEdits,
                  canEditMedicalOpinionDraftSuggestions:
                    !isInlineSuggestionsView &&
                    canCreateSuggestions &&
                    canManageMoRemarks,
                  onAddSuggestion:
                    !isInlineSuggestionsView &&
                    !useInlineSuggestionMode &&
                    canCreateSuggestions
                      ? handleAddSuggestion
                      : undefined,
                  onAcceptSuggestion:
                    !isInlineSuggestionsView && canAcceptSuggestions
                      ? handleAcceptSuggestion
                      : undefined,
                  onSaveSuggestionDraft:
                    !isInlineSuggestionsView &&
                    canCreateSuggestions &&
                    canManageMoRemarks
                      ? handleSaveSuggestionDraft
                      : undefined,
                  onRejectSuggestion:
                    !isInlineSuggestionsView && canAcceptSuggestions
                      ? handleRejectSuggestion
                      : undefined,
                  onDismissSuggestion:
                    !isInlineSuggestionsView && canDismissSuggestionEdits
                      ? handleDismissSuggestion
                      : undefined,
                  onSearchMissingDetail: canCreateSuggestions
                    ? handleSearchMissingSuggestionDetail
                    : undefined,
                  suggestionFieldIds: filteredSuggestionFieldIds,
                  showStatusFilter: !isProviderPendingSuggestionView,
                  statusSummaryText:
                    isProviderPendingSuggestionView &&
                    totalSuggestionCount > pendingSuggestionCount
                      ? `${pendingSuggestionCount} of ${totalSuggestionCount} remaining`
                      : null,
                  openNewSuggestionComposer:
                    pendingSuggestionComposerFieldId === activeReviewFieldId &&
                    activeReviewTab === 'suggestions',
                  isSubmitting:
                    createSuggestionMutation.isPending ||
                    saveRemarksFullTextMutation.isPending ||
                    acceptSuggestionMutation.isPending ||
                    rejectSuggestionMutation.isPending ||
                    dismissSuggestionMutation.isPending,
                },
              }
            : null
        }
      />
      <UserDbqEditorRequestFlowDialogs
        inlineMissingDetailSuggestion={inlineMissingDetailSuggestion}
        closeInlineMissingDetailModal={closeInlineMissingDetailModal}
        inlineMissingDetailInputs={inlineMissingDetailInputs}
        inlineMissingInputValues={inlineMissingInputValues}
        inlineMissingInputSearches={inlineMissingInputSearches}
        isInlineMissingDetailSaving={isInlineMissingDetailSaving}
        handleSearchInlineMissingInputValue={
          handleSearchInlineMissingInputValue
        }
        handleInlineMissingInputValueChange={
          handleInlineMissingInputValueChange
        }
        handleApplyInlineMissingInputValue={handleApplyInlineMissingInputValue}
        inlineMissingDetailDraftText={inlineMissingDetailDraftText}
        setInlineMissingDetailDraftText={setInlineMissingDetailDraftText}
        handleSaveInlineMissingDetail={handleSaveInlineMissingDetail}
        inlineRejectedSuggestionsFieldId={inlineRejectedSuggestionsFieldId}
        closeInlineRejectedSuggestionsModal={
          closeInlineRejectedSuggestionsModal
        }
        getFieldLabelByKey={getFieldLabelByKey}
        inlineRejectedSuggestions={inlineRejectedSuggestions}
        inlineEvidenceSuggestion={inlineEvidenceSuggestion}
        closeInlineEvidenceModal={closeInlineEvidenceModal}
        inlineEvidenceFieldId={inlineEvidenceFieldId}
        inlineEvidenceRefs={inlineEvidenceRefs}
        openInlineEvidencePage={openInlineEvidencePage}
        inlineEvidenceTranscriptRefs={inlineEvidenceTranscriptRefs}
        inlineEvidencePage={inlineEvidencePage}
        closeInlineEvidencePage={closeInlineEvidencePage}
        userDbq={userDbq}
        inlineGenerationFailureNotice={inlineGenerationFailureNotice}
        closeInlineGenerationFailureModal={closeInlineGenerationFailureModal}
        inlineGenerationFailureFieldId={inlineGenerationFailureFieldId}
        canManageMedicalOpinion={canManageMedicalOpinion}
        showMoEvidenceModal={showMoEvidenceModal}
        setShowMoEvidenceModal={setShowMoEvidenceModal}
        userDbqId={userDbqId}
        autoMedicalOpinionData={autoMedicalOpinionData}
        isManagedMedicalOpinionRemarks={isManagedMedicalOpinionRemarks}
        showRemarksEditorModal={showRemarksEditorModal}
        setShowRemarksEditorModal={setShowRemarksEditorModal}
        setRemarksEditorSubmitMode={setRemarksEditorSubmitMode}
        remarksEditorText={remarksEditorText}
        setRemarksEditorText={setRemarksEditorText}
        canManageMoRemarks={canManageMoRemarks}
        handleSaveRemarksEditor={handleSaveRemarksEditor}
        saveRemarksFullTextMutation={saveRemarksFullTextMutation}
        remarksEditorSubmitMode={remarksEditorSubmitMode}
        remarksFieldInfo={remarksFieldInfo}
        showMoGenerationModal={showMoGenerationModal}
        setShowMoGenerationModal={setShowMoGenerationModal}
        hasMoEvidence={hasMoEvidence}
        moRequestContentions={moRequestContentions}
        hasMissingMoToe={hasMissingMoToe}
        moGenerationPromptInput={moGenerationPromptInput}
        setMoGenerationPromptInput={setMoGenerationPromptInput}
        requestMoGenerationMutation={requestMoGenerationMutation}
        handleRequestMoGeneration={handleRequestMoGeneration}
        isMoGenerationActive={isMoGenerationActive}
        canStartMoGeneration={canStartMoGeneration}
        canToggleRequiredPerDbq={canToggleRequiredPerDbq}
        showRequiredFieldModal={showRequiredFieldModal}
        handleCancelRequiredModal={handleCancelRequiredModal}
        pendingFieldStatus={pendingFieldStatus}
        handleApplyRequiredToTemplate={handleApplyRequiredToTemplate}
        updateRequiredFieldsMutation={updateRequiredFieldsMutation}
        handleApplyRequiredToUserDbq={handleApplyRequiredToUserDbq}
        updateUserDbqRequiredFieldsMutation={
          updateUserDbqRequiredFieldsMutation
        }
        isProvider={isProvider}
        showSigningModal={showSigningModal}
        setShowSigningModal={setShowSigningModal}
        isProviderConfirmingChanges={isProviderConfirmingChanges}
        providerProfiles={providerProfiles}
        selectedProfileId={selectedProfileId}
        handleProfileSelect={handleProfileSelect}
        providerProfile={providerProfile}
        certificationData={certificationData}
        handleCertificationChange={handleCertificationChange}
        providerSignActionLabel={providerSignActionLabel}
        handleSignSubmit={handleSignSubmit}
        signDbqMutation={signDbqMutation}
        showNewlyRequiredModal={showNewlyRequiredModal}
        setShowNewlyRequiredModal={setShowNewlyRequiredModal}
        newlyRequiredFields={newlyRequiredFields}
        handleGoToRequiredField={handleGoToRequiredField}
        showFieldConfirmation={showFieldConfirmation}
        cancelFieldChange={cancelFieldChange}
        pendingFieldChange={pendingFieldChange}
        confirmFieldChange={confirmFieldChange}
      />
    </>
  );
};

export default UserDbqEditor;
